import React from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Plus, Save, Trash2 } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { YesNoDialog } from '@/components/ui/modules/yes-no-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { Medcall2ParticipantIdentityCard } from './Medcall2ParticipantIdentityCard';
import { Medcall2StaffAssignmentForm } from './Medcall2StaffAssignmentForm';
import { Medcall2ParticipantStatusPopover } from './Medcall2ParticipantStatusPopover';
import { Medcall2ClientNotesDialog } from './Medcall2ClientNotesDialog';
import { readMedcall2IntakeOptions, readMedcall2ProviderOptions, type Medcall2OptionRow } from './medcall2IntakeOptions';
import './Medcall2IdentityDialogs.css';
import { Medcall2PersonAssignmentForm, Medcall2PersonAssignmentSubmission } from './Medcall2PersonAssignmentForm';
import { MedcallAdministrationNotesEditor } from './MedcallAdministrationNotesEditor';
import { MedcallImlaiDueDateWarning } from './MedcallImlaiDueDateWarning';
import { normalizeMedcallImlaiDate, type MedcallImlaiDueDateUpdate } from './medcallImlaiDueDateWarning';
import { MedcallParticipantActions } from './MedcallParticipantActions';
import {
  MedcallAdherenceReportDialog,
  MedcallMedicationListDialog,
} from './MedcallParticipantReports';
import { MedcallSendMessageDialog } from './MedcallSendMessageDialog';
import {
  getMedcallStrengthOptionLabel,
  getMedcallStrengthOptionValue,
  resolveMedcallStoredStrength,
  resolveMedcallStrengthControlValue,
  resolveStoredMedicationExternalId,
  sortMedcallStrengthOptionsNumerically,
} from './medcallMedicationCatalogSelection';
import {
  getMedcallStaffOptionLabel,
  orderMedcallStaffOptions,
} from './medcallStaffOptions';
import type { MedcallStaffOption } from './medcallStaffOptions';
import { buildLegacyMedcallMedicationConfirmationRows } from './medcallMedicationConfirmation';
import {
  buildMedcallProfileMutationVariables,
  getMedcallIdentityDialogCopy,
  getMedcallProfileFacilityProgram,
  normalizeMedcallProfileCidDraft,
  shouldShowMedcallAliasEntryFields,
} from './medcallParticipantIdentityCompatibility';
import {
  didMedcall2TimelineMutationComplete,
  getLegacyMedcallMedicationWarning,
  getLegacyMedcallTimelineFrequencyLabel,
  getLegacyMedcallTimelineSubmissionTime,
  getMedcallTimelinePage,
  getVisibleMedcallTimelineRows,
  isLegacyMedcallMedicationRunOut,
  shouldShowMedcallMedicationHistory,
  userCanEditMedcallTimelineSite,
  userCanEditMedcallTimelineStatus,
  userCanEditMedcallTimelineWitness,
} from './medcallTimelineCompatibility';

import { MEDCALL2_SEARCH_MEDICATIONS, MEDCALL2_MEDICATION_STRENGTHS } from './medcall2CatalogQueries';
import { createMedcallLatestRequestGuard } from './medcallLatestRequestGuard';

const MEDCALL2_PROFILE_OPTIONS = gql`
  query Medcall2ProfileOptions {
    medcallOptions: medcall2Options(names: ["programs", "facilities", "providers", "filter_domains"]) {
      option_name
      option_value_json
    }
  }
`;

const MEDCALL_PARTICIPANT_DETAIL_PAGE = gql`
  query MedcallParticipantDetailPage(
    $includeTimelines: Boolean
    $odrparticipant: Int
    $participantId: Int
    $preserveLegacyMedicationOrder: Boolean
  ) {
    medcallParticipantActionMeta {
      facilityOptions {
        label
        value
      }
      filterDomains
      programOptions {
        label
        value
      }
      providerOptions {
        label
        value
      }
      staffOptions {
        email
        fullname
        id
        username
      }
    }
    medcallParticipantDetail(
      includeTimelines: $includeTimelines
      odrparticipant: $odrparticipant
      participantId: $participantId
      preserveLegacyMedicationOrder: $preserveLegacyMedicationOrder
    ) {
      active
      activation_history {
        date
        deactivation_reason
        fullname
        status
        username
      }
      aliases {
        firstname
        lastname
      }
      cid
      created_at
      dob
      facility
      firstname
      history_ids {
        date
        fullname
        id
        username
      }
      id
      lastname
      medications {
        active
        days_of_week
        frequency
        id
        importance_level
        injection_info {
          administration_notes
          date_due
          date_given
          date_injection_arrived
          on_site
        }
        last_taken_date
        medicine_detail {
          drugbank_pcid
          external_id
          hits {
            value
          }
          name
          uuid
        }
        medicine_name
        prn
        psychotropic_meds
        refill_status
        refills {
          notify_week
          refill_date
        }
        route
        start_date
        stop_date
        stop_date_status
        strength
        timelines {
          date
          dose
          id
          injection_info {
            administration_notes
            date_due
            date_given
            date_injection_arrived
            on_site
          }
          med_id
          mparticipant_id
          notification_type_id
          refilled_at
          route
          site
          status
          strength
          taken_at
          time
          times_per_day
          witnessed_by_other
        }
        times_doses {
          dose
          time
        }
        times_per_day
        user_id
        uuid
      }
      notes {
        entryby
        entrydate
        id
        notes
      }
      nurse_name {
        email
        firstname
        lastname
        title
      }
      option_facility_id
      participant_id
      program
      project_sites_client_options {
        id
        week_start
      }
      project_sites_medication_statuses {
        date
        id
        notes
        status
      }
      provider
      psychiatrist {
        email
        firstname
        lastname
        title
      }
      staff_assigned
      updated_at
      user_id
      view_user_ids
    }
  }
`;

const MEDCALL_MEDICATION_TIMELINES = gql`
  query MedcallMedicationTimelines($medicationId: Int!) {
    medcallMedicationTimelines(medicationId: $medicationId) {
      date
      dose
      id
      injection_info {
        administration_notes
        date_due
        date_given
        date_injection_arrived
        on_site
      }
      med_id
      mparticipant_id
      notification_type_id
      refilled_at
      route
      site
      status
      strength
      taken_at
      time
      times_per_day
      witnessed_by_other
    }
  }
`;

const MEDCALL_COUNTY_HOLIDAYS = gql`
  query MedcallCountyHolidays {
    medcallCountyHolidays {
      date
      holiday
    }
  }
`;

const MEDCALL_ADD_TO_MY_CLIENTS = gql`
  mutation MedcallAddToMyClients($participantId: Int!) {
    medcallAddToMyClients(participantId: $participantId) {
      message
      responseCode
      responseID
    }
  }
`;

const MEDCALL_REMOVE_FROM_MY_CLIENTS = gql`
  mutation MedcallRemoveFromMyClients($participantId: Int!) {
    medcallRemoveFromMyClients(participantId: $participantId) {
      message
      responseCode
      responseID
    }
  }
`;

const MEDCALL_UPDATE_PARTICIPANT_PROFILE = gql`
  mutation MedcallUpdateParticipantProfile(
    $cid: String
    $dob: String
    $optionFacilityId: Int
    $participantId: Int!
    $preserveLegacySemantics: Boolean
    $program: String
    $provider: String
  ) {
    medcallUpdateParticipantProfile(
      cid: $cid
      dob: $dob
      optionFacilityId: $optionFacilityId
      participantId: $participantId
      preserveLegacySemantics: $preserveLegacySemantics
      program: $program
      provider: $provider
    ) {
      id
    }
  }
`;

const MEDCALL_UPDATE_PARTICIPANT_STAFF = gql`
  mutation MedcallUpdateParticipantStaff($newStaffId: Int!, $participantId: Int!) {
    medcallUpdateParticipantStaff(newStaffId: $newStaffId, participantId: $participantId) {
      id
    }
  }
`;

const MEDCALL_UPDATE_PARTICIPANT_NURSE = gql`
  mutation MedcallUpdateParticipantNurse(
    $email: String!
    $firstname: String!
    $lastname: String!
    $participantId: Int!
    $title: String!
  ) {
    medcallUpdateParticipantNurse(
      email: $email
      firstname: $firstname
      lastname: $lastname
      participantId: $participantId
      title: $title
    ) {
      id
    }
  }
`;

const MEDCALL_UPDATE_PARTICIPANT_PSYCHIATRIST = gql`
  mutation MedcallUpdateParticipantPsychiatrist(
    $email: String
    $firstname: String!
    $lastname: String!
    $participantId: Int!
    $title: String!
  ) {
    medcallUpdateParticipantPsychiatrist(
      email: $email
      firstname: $firstname
      lastname: $lastname
      participantId: $participantId
      title: $title
    ) {
      id
    }
  }
`;

const MEDCALL_ADD_PARTICIPANT_NOTE = gql`
  mutation MedcallAddParticipantNote($note: String!, $participantId: Int!) {
    medcallAddParticipantNote(note: $note, participantId: $participantId) {
      id
    }
  }
`;

const MEDCALL_ADD_PARTICIPANT_ALIAS = gql`
  mutation MedcallAddParticipantAlias($firstname: String!, $lastname: String!, $participantId: Int!) {
    medcallAddParticipantAlias(firstname: $firstname, lastname: $lastname, participantId: $participantId) {
      id
    }
  }
`;

const MEDCALL_DELETE_PARTICIPANT_NOTE = gql`
  mutation MedcallDeleteParticipantNote($noteId: Int!, $participantId: Int!) {
    medcallDeleteParticipantNote(noteId: $noteId, participantId: $participantId) {
      message
      responseCode
      responseID
    }
  }
`;

const MEDCALL_SET_PARTICIPANT_ACTIVE = gql`
  mutation MedcallSetParticipantActive(
    $activationBackDate: String
    $active: Boolean!
    $deactivationReason: String
    $participantId: Int!
  ) {
    medcallSetParticipantActive(
      activationBackDate: $activationBackDate
      active: $active
      deactivationReason: $deactivationReason
      participantId: $participantId
    ) {
      id
    }
  }
`;

const MEDCALL_CREATE_MEDICATION = gql`
  mutation MedcallCreateMedication(
    $administrationNotes: String
    $daysOfWeek: [String]
    $frequency: String!
    $importanceLevel: Int
    $injectionDateArrived: String
    $injectionDateDue: String
    $injectionDateGiven: String
    $injectionOnSite: Int
    $medicineDetail: String
    $medicineName: String!
    $participantId: Int!
    $pharmabankUuid: String
    $prn: Boolean
    $psychotropicMeds: Boolean
    $refillDate: String
    $refillStatus: Boolean
    $route: String!
    $startDate: String
    $stopDate: String
    $stopDateStatus: Boolean
    $strength: String!
    $timesPerDay: String!
  ) {
    medcallCreateMedication(
      administrationNotes: $administrationNotes
      daysOfWeek: $daysOfWeek
      frequency: $frequency
      importanceLevel: $importanceLevel
      injectionDateArrived: $injectionDateArrived
      injectionDateDue: $injectionDateDue
      injectionDateGiven: $injectionDateGiven
      injectionOnSite: $injectionOnSite
      medicineDetail: $medicineDetail
      medicineName: $medicineName
      participantId: $participantId
      pharmabankUuid: $pharmabankUuid
      prn: $prn
      psychotropicMeds: $psychotropicMeds
      refillDate: $refillDate
      refillStatus: $refillStatus
      route: $route
      startDate: $startDate
      stopDate: $stopDate
      stopDateStatus: $stopDateStatus
      strength: $strength
      timesPerDay: $timesPerDay
    ) {
      id
    }
  }
`;

const MEDCALL_UPDATE_MEDICATION = gql`
  mutation MedcallUpdateMedication(
    $administrationNotes: String
    $daysOfWeek: [String]
    $frequency: String!
    $importanceLevel: Int
    $injectionDateArrived: String
    $injectionDateDue: String
    $injectionDateGiven: String
    $injectionOnSite: Int
    $medicationId: Int!
    $medicineDetail: String
    $medicineName: String!
    $pharmabankUuid: String
    $prn: Boolean
    $psychotropicMeds: Boolean
    $refillDate: String
    $refillStatus: Boolean
    $route: String!
    $startDate: String
    $stopDate: String
    $stopDateStatus: Boolean
    $strength: String!
    $timesPerDay: String!
  ) {
    medcallUpdateMedication(
      administrationNotes: $administrationNotes
      daysOfWeek: $daysOfWeek
      frequency: $frequency
      importanceLevel: $importanceLevel
      injectionDateArrived: $injectionDateArrived
      injectionDateDue: $injectionDateDue
      injectionDateGiven: $injectionDateGiven
      injectionOnSite: $injectionOnSite
      medicationId: $medicationId
      medicineDetail: $medicineDetail
      medicineName: $medicineName
      pharmabankUuid: $pharmabankUuid
      prn: $prn
      psychotropicMeds: $psychotropicMeds
      refillDate: $refillDate
      refillStatus: $refillStatus
      route: $route
      startDate: $startDate
      stopDate: $stopDate
      stopDateStatus: $stopDateStatus
      strength: $strength
      timesPerDay: $timesPerDay
    ) {
      id
    }
  }
`;

const MEDCALL_UPDATE_IMLAI_DUE_DATE = gql`
  mutation MedcallUpdateImlaiDueDate($dueDate: String!, $medicationId: Int!) {
    medcallUpdateImlaiDueDate(dueDate: $dueDate, medicationId: $medicationId) {
      id
      injection_info {
        date_due
      }
    }
  }
`;

const MEDCALL_SEARCH_MEDICATIONS = gql`
  query MedcallSearchMedications($searchText: String!) {
    medcallSearchMedications(searchText: $searchText) {
      displayName
      drugbankPcid
      externalId
      hits {
        value
      }
      isCustom
      level
      medicineDetail
      name
      source
      uuid
    }
  }
`;

const MEDCALL_MEDICATION_STRENGTHS = gql`
  query MedcallMedicationStrengths($externalId: String!, $medicineName: String) {
    medcallMedicationStrengths(externalId: $externalId, medicineName: $medicineName) {
      displayName
      externalId
      isCustom
      name
      strength
    }
  }
`;

const MEDCALL_SET_MEDICATION_ACTIVE = gql`
  mutation MedcallSetMedicationActive($active: Boolean!, $medicationId: Int!) {
    medcallSetMedicationActive(active: $active, medicationId: $medicationId) {
      id
    }
  }
`;

const MEDCALL_DELETE_MEDICATION = gql`
  mutation MedcallDeleteMedication($medicationId: Int!) {
    medcallDeleteMedication(medicationId: $medicationId) {
      message
      responseCode
      responseID
    }
  }
`;

const MEDCALL_UPDATE_MEDICATION_TIMELINE = gql`
  mutation MedcallUpdateMedicationTimeline(
    $preserveLegacySemantics: Boolean
    $status: String
    $time: String
    $timelineId: Int!
    $witnessedByOther: Boolean
  ) {
    medcallUpdateMedicationTimeline(
      preserveLegacySemantics: $preserveLegacySemantics
      status: $status
      time: $time
      timelineId: $timelineId
      witnessedByOther: $witnessedByOther
    ) {
      id
    }
  }
`;

const MEDCALL_UPDATE_MEDICATION_TIMELINE_SITE = gql`
  mutation MedcallUpdateMedicationTimelineSite(
    $preserveLegacySemantics: Boolean
    $site: String
    $timelineId: Int!
  ) {
    medcallUpdateMedicationTimelineSite(
      preserveLegacySemantics: $preserveLegacySemantics
      site: $site
      timelineId: $timelineId
    ) {
      id
    }
  }
`;

const MEDCALL_UPDATE_MEDICATION_ADMINISTRATION_NOTES = gql`
  mutation MedcallUpdateMedicationAdministrationNotes(
    $administrationNotes: String!
    $preserveLegacySemantics: Boolean
    $timelineId: Int!
  ) {
    medcallUpdateMedicationAdministrationNotes(
      administrationNotes: $administrationNotes
      preserveLegacySemantics: $preserveLegacySemantics
      timelineId: $timelineId
    ) {
      id
      injection_info {
        administration_notes
      }
    }
  }
`;

const MEDCALL_UPDATE_PROJECT_SITES_MEDICATION_STATUSES = gql`
  mutation MedcallUpdateProjectSitesMedicationStatuses(
    $endDate: String
    $notes: String
    $participantId: Int!
    $startDate: String!
    $status: String!
  ) {
    medcallUpdateProjectSitesMedicationStatuses(
      endDate: $endDate
      notes: $notes
      participantId: $participantId
      startDate: $startDate
      status: $status
    ) {
      id
      date
      status
    }
  }
`;

const MEDCALL_UPDATE_PROJECT_SITES_CLIENT_OPTIONS = gql`
  mutation MedcallUpdateProjectSitesClientOptions($participantId: Int!, $weekStart: Int!) {
    medcallUpdateProjectSitesClientOptions(participantId: $participantId, weekStart: $weekStart) {
      id
      week_start
    }
  }
`;

const ACTION_LINK_GROUP_CLASS =
  '[&_button]:!h-auto [&_button]:!rounded-none [&_button]:!border-0 [&_button]:!bg-transparent [&_button]:!px-0 [&_button]:!py-0 [&_button]:!shadow-none ' +
  '[&_button]:whitespace-nowrap [&_button]:text-sm [&_button]:font-medium [&_button]:leading-5 [&_button]:text-[var(--azure-primary)] [&_button:hover]:!bg-transparent [&_button:hover]:underline [&_button:hover]:underline-offset-2 ' +
  '[&_button+button]:ml-2 [&_button+button]:border-l [&_button+button]:border-[var(--neutral-gray-70)] [&_button+button]:pl-2';
const SELECT_EMPTY_VALUE = '__empty__';
const NURSELESS_PROGRAMS = ['ODRH Scattered/ERC', 'FIST ERC', 'DSH ERC'];
const PROGRAM_LEVEL_MANAGEMENT_PROGRAMS = [...NURSELESS_PROGRAMS, 'Project Sites'];
const NURSE_TITLES = ['LVN', 'MA', 'CNA', 'RN', 'LPT', 'NP', 'EMT'];
const PSYCHIATRIST_TITLES = ['MD', 'NP', 'PA'];
const DEACTIVATION_OPTIONS = ['AWOL', 'Hospitalization/Treatment Facility', 'Re-arrest', 'Other'];
const MEDICATION_ROUTE_OPTIONS = ['AU', 'AS', 'AD', 'PO', 'IM-LAI', 'IM-Other', 'Subq', 'Rectally', 'IV', 'OD', 'OS', 'OU', 'INH', 'SUPP', 'Nasal', 'SL', 'TD', 'TOP', 'VAG', 'SUBQ LAI'];
const MEDICATION_TIMELINE_SITE_OPTIONS = [
  { label: 'Not Set', value: '' },
  { label: 'Left Deltoid', value: 'left-deltoid' },
  { label: 'Right Deltoid', value: 'right-deltoid' },
  { label: 'Left Gluteal', value: 'left-gluteal' },
  { label: 'Right Gluteal', value: 'right-gluteal' },
];
const MEDICATION_DAY_OPTIONS = [
  { label: 'Mon', value: 'monday' },
  { label: 'Tue', value: 'tuesday' },
  { label: 'Wed', value: 'wednesday' },
  { label: 'Thu', value: 'thursday' },
  { label: 'Fri', value: 'friday' },
  { label: 'Sat', value: 'saturday' },
  { label: 'Sun', value: 'sunday' },
];
const MEDICATION_TIME_OPTION_LABELS: Record<string, string> = {
  bid: 'BID',
  'q15min-int': 'Q15MIN-INT',
  q2h: 'Q2H',
  q4h: 'Q4H',
  q6h: 'Q6H',
  q8h: 'Q8H',
  qam: 'QAM',
  qday: 'QDAY',
  qhs: 'QHS',
  qid: 'QID',
  qod: 'QOD',
  tid: 'TID',
};
const PROJECT_SITES_TRACKER_VIEW_OPTIONS = [
  { label: 'Day', value: 'day' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
] as const;
const PROJECT_SITES_TRACKER_STATUS_OPTIONS = [
  { label: 'Taken', value: 'taken' },
  { label: 'Not Taken', value: 'notTaken' },
];
const PROJECT_SITES_WEEK_START_OPTIONS = [
  { label: 'Sunday - Saturday', value: 0 },
  { label: 'Monday - Sunday', value: 1 },
  { label: 'Tuesday - Monday', value: 2 },
  { label: 'Wednesday - Tuesday', value: 3 },
  { label: 'Thursday - Wednesday', value: 4 },
  { label: 'Friday - Thursday', value: 5 },
  { label: 'Saturday - Friday', value: 6 },
];
const MEDICATION_FREQUENCY_OPTIONS = [
  { label: 'Every Day', scheduleOptions: ['qday', 'qam', 'qhs', 'bid', 'tid', 'qid', 'q2h', 'q4h', 'q6h', 'q8h'], value: 'everyday' },
  { label: 'Every Other Day', scheduleOptions: ['qod'], value: 'everyotherday' },
  { label: 'Once', scheduleOptions: ['qday'], value: 'once' },
  { label: 'Q15MIN-INT', scheduleOptions: ['q15min-int'], value: 'q15min-int' },
  { label: 'QWK', scheduleOptions: ['qday'], value: 'qwk' },
  { label: 'Q2WK', scheduleOptions: ['qday'], value: 'q2wk' },
  { label: 'Q3WK', scheduleOptions: ['qday'], value: 'q3wk' },
  { label: 'Q4WK', scheduleOptions: ['qday'], value: 'q4wk' },
  { label: 'Q5WK', scheduleOptions: ['qday'], value: 'q5wk' },
  { label: 'Q6WK', scheduleOptions: ['qday'], value: 'q6wk' },
  { label: 'Q7WK', scheduleOptions: ['qday'], value: 'q7wk' },
  { label: 'Q8WK', scheduleOptions: ['qday'], value: 'q8wk' },
  { label: 'Q10WK', scheduleOptions: ['qday'], value: 'q10wk' },
  { label: 'Q2MONTH', scheduleOptions: ['qday'], value: 'q2month' },
  { label: 'Q3MONTH', scheduleOptions: ['qday'], value: 'q3month' },
  { label: 'Q4MONTH', scheduleOptions: ['qday'], value: 'q4month' },
  { label: 'Q6MONTH', scheduleOptions: ['qday'], value: 'q6month' },
  { label: 'Custom Week Days', scheduleOptions: ['qday', 'qam', 'qhs', 'bid', 'tid', 'qid', 'q2h', 'q4h', 'q6h', 'q8h'], value: 'custom_days' },
];
const IM_LAI_FILTERED_FREQUENCIES = new Set(['everyday', 'custom_days']);

export type MedcallBootstrapContext = {
  defaultLandingPath?: string | null;
  hasMedcallPowerAccess?: boolean;
  hasSuperAccess?: boolean;
  isAdminUser?: boolean;
  medcallUserId: number;
};

type MedcallPerson = {
  email?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  title?: string | null;
};

type MedcallAlias = {
  firstname?: string | null;
  lastname?: string | null;
};

type MedcallActivationHistoryItem = {
  date?: string | null;
  deactivation_reason?: string | null;
  fullname?: string | null;
  status?: string | null;
  username?: string | null;
};

type MedcallStaffHistoryItem = {
  date?: string | null;
  fullname?: string | null;
  id?: number | null;
  username?: string | null;
};

type MedcallMedicationRefill = {
  notify_week?: string | null;
  refill_date?: string | null;
};

type MedcallMedicationDetail = {
  drugbank_pcid?: string | null;
  external_id?: string | null;
  hits?: Array<{ value?: string | null }> | null;
  name?: string | null;
  uuid?: string | null;
};

type MedcallMedicationSearchItem = {
  displayName?: string | null;
  drugbankPcid?: string | null;
  externalId?: string | null;
  hits?: Array<{ value?: string | null }> | null;
  isCustom?: boolean | null;
  level?: number | null;
  medicineDetail?: string | null;
  name?: string | null;
  source?: string | null;
  uuid?: string | null;
};

type MedcallMedicationStrengthOption = {
  displayName?: string | null;
  externalId?: string | null;
  isCustom?: boolean | null;
  name?: string | null;
  strength?: string | null;
};

type MedcallMedicationSearchData = {
  medcallSearchMedications?: MedcallMedicationSearchItem[] | null;
};

type MedcallMedicationStrengthsData = {
  medcallMedicationStrengths?: MedcallMedicationStrengthOption[] | null;
};

type MedcallMedicationTimesDose = {
  dose?: string | null;
  time?: string | null;
};

type MedcallMedicationInjectionInfo = {
  administration_notes?: string | null;
  date_due?: string | null;
  date_given?: string | null;
  date_injection_arrived?: string | null;
  on_site?: number | null;
};

type MedcallMedicationTimeline = {
  date?: string | null;
  dose?: string | null;
  id: number;
  injection_info?: MedcallMedicationInjectionInfo[] | null;
  med_id?: number | null;
  mparticipant_id?: number | null;
  notification_type_id?: number | null;
  refilled_at?: string | null;
  route?: string | null;
  site?: string | null;
  status?: string | null;
  strength?: string | null;
  taken_at?: string | null;
  time?: string | null;
  times_per_day?: string | null;
  witnessed_by_other?: boolean | null;
};

type MedcallParticipantMedication = {
  active?: boolean | null;
  days_of_week?: string[] | null;
  frequency?: string | null;
  id: number;
  importance_level?: number | null;
  injection_info?: MedcallMedicationInjectionInfo[] | null;
  last_taken_date?: string | null;
  medicine_detail?: MedcallMedicationDetail[] | null;
  medicine_name?: string | null;
  prn?: boolean | null;
  psychotropic_meds?: boolean | null;
  refill_status?: boolean | null;
  refills?: MedcallMedicationRefill[] | null;
  route?: string | null;
  start_date?: string | null;
  stop_date?: string | null;
  stop_date_status?: string | null;
  strength?: string | null;
  timelines?: MedcallMedicationTimeline[] | null;
  times_doses?: MedcallMedicationTimesDose[] | null;
  times_per_day?: string | null;
  user_id?: number | null;
  uuid?: string | null;
};

type MedcallParticipantNote = {
  entryby?: string | null;
  entrydate?: string | null;
  id: number;
  notes?: string | null;
};

type MedcallProjectSitesMedicationStatus = {
  date?: string | null;
  id: number;
  notes?: string | null;
  status?: string | null;
};

type MedcallProjectSitesClientOptions = {
  id: number;
  week_start?: number | null;
};

type MedcallListOption = {
  label?: string | null;
  value?: string | null;
};

type MedcallParticipantDetail = {
  active?: boolean | null;
  activation_history?: MedcallActivationHistoryItem[] | null;
  aliases?: MedcallAlias[] | null;
  cid?: string | null;
  created_at?: string | null;
  dob?: string | null;
  facility?: string | null;
  firstname?: string | null;
  history_ids?: MedcallStaffHistoryItem[] | null;
  id: number;
  lastname?: string | null;
  medications?: MedcallParticipantMedication[] | null;
  notes?: MedcallParticipantNote[] | null;
  nurse_name?: MedcallPerson | null;
  option_facility_id?: number | null;
  participant_id?: number | null;
  program?: string | null;
  project_sites_client_options?: MedcallProjectSitesClientOptions | null;
  project_sites_medication_statuses?: MedcallProjectSitesMedicationStatus[] | null;
  provider?: string | null;
  psychiatrist?: MedcallPerson | null;
  staff_assigned?: string | null;
  updated_at?: string | null;
  user_id?: number | null;
  view_user_ids?: string[] | null;
};

type MedcallParticipantActionMeta = {
  facilityOptions?: MedcallListOption[] | null;
  filterDomains?: string[] | null;
  programOptions?: MedcallListOption[] | null;
  providerOptions?: MedcallListOption[] | null;
  staffOptions?: MedcallStaffOption[] | null;
};

type MedcallParticipantDetailData = {
  medcallParticipantActionMeta: MedcallParticipantActionMeta | null;
  medcallParticipantDetail: MedcallParticipantDetail | null;
};

type MedcallCountyHolidaysData = {
  medcallCountyHolidays?: Array<{
    date?: string | null;
    holiday?: string | null;
  } | null> | null;
};

type RouteLocationState = {
  from?: string;
  openDialog?: 'medication' | 'message';
};

type MedcallMedicationView = 'active' | 'all' | 'historical' | 'prn';

type ProjectSitesTrackerView = 'day' | 'month' | 'week';

type ProjectSitesTrackerSelection = {
  endDate: string | null;
  label: string;
  startDate: string;
};

type ProfileDraft = {
  cid: string;
  dob: string;
  optionFacilityId: string;
  program: string;
  provider: string;
};

type PersonDraft = {
  confirmEmail: string;
  email: string;
  firstname: string;
  lastname: string;
  title: string;
};

type StatusDraft = {
  activationBackDate: string;
  otherReason: string;
  reason: string;
};

type MedicationDraft = {
  administrationNotes: string;
  customDays: string[];
  dateDue: string;
  dateGiven: string;
  dateInjectionArrived: string;
  frequency: string;
  importanceLevel: string;
  medicationExternalId: string;
  medicineDetail: string;
  medicineName: string;
  onSite: string;
  prn: boolean;
  psychotropicMeds: boolean;
  refillDate: string;
  refillStatus: boolean;
  route: string;
  startDate: string;
  stopDate: string;
  stopDateEnabled: boolean;
  strength: string;
  timesPerDay: string;
};

type MedicationSaveVariables = {
  administrationNotes: string | null;
  daysOfWeek: string[];
  frequency: string;
  importanceLevel: number;
  injectionDateArrived: string | null;
  injectionDateDue: string | null;
  injectionDateGiven: string | null;
  injectionOnSite: number;
  medicineDetail: string | null;
  medicineName: string;
  participantId: number;
  prn: boolean;
  psychotropicMeds: boolean;
  refillDate: string | null;
  refillStatus: boolean;
  route: string;
  startDate: string | null;
  stopDate: string | null;
  stopDateStatus: boolean;
  strength: string;
  timesPerDay: string;
};

const normalizeValue = (value: unknown) => `${value || ''}`.trim();

const normalizeEmail = (value: unknown) => normalizeValue(value).toLowerCase();

const padDatePart = (value: number) => `${value}`.padStart(2, '0');

const parseDisplayDate = (value: string) => {
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (isoMatch) {
    return {
      day: isoMatch[3],
      hour: isoMatch[4] ? Number(isoMatch[4]) : null,
      minute: isoMatch[5] || null,
      month: isoMatch[2],
      year: isoMatch[1],
    };
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return {
    day: padDatePart(parsedDate.getUTCDate()),
    hour: parsedDate.getUTCHours(),
    minute: padDatePart(parsedDate.getUTCMinutes()),
    month: padDatePart(parsedDate.getUTCMonth() + 1),
    year: `${parsedDate.getUTCFullYear()}`,
  };
};

const formatDate = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const displayDate = parseDisplayDate(normalizedValue);
  if (!displayDate) {
    return normalizedValue || '—';
  }

  return `${displayDate.month}/${displayDate.day}/${displayDate.year}`;
};

const formatDateTime = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const displayDate = parseDisplayDate(normalizedValue);
  if (!displayDate) {
    return normalizedValue || '—';
  }

  const dateLabel = `${displayDate.month}/${displayDate.day}/${displayDate.year}`;
  if (displayDate.hour === null || !displayDate.minute) {
    return dateLabel;
  }

  const hourValue = displayDate.hour;
  const minuteValue = displayDate.minute;
  const suffix = hourValue >= 12 ? 'PM' : 'AM';
  const normalizedHour = hourValue % 12 || 12;
  return `${dateLabel} ${normalizedHour}:${minuteValue} ${suffix}`;
};

const formatPersonLabel = (person: MedcallPerson | null | undefined) => {
  if (!person) {
    return '—';
  }

  const pieces = [normalizeValue(person.firstname), normalizeValue(person.lastname)].filter(Boolean);
  const title = normalizeValue(person.title);
  if (title) {
    pieces.push(title);
  }

  if (pieces.length < 1) {
    return '—';
  }

  return pieces.join(', ');
};

const formatLegacyPersonLabel = (person: MedcallPerson | null | undefined) => {
  if (!person) {
    return '—';
  }

  const name = [normalizeValue(person.firstname), normalizeValue(person.lastname)]
    .filter(Boolean)
    .join(' ');
  const title = normalizeValue(person.title);
  return [name, title].filter(Boolean).join(', ') || '—';
};

const formatAliases = (aliases: MedcallAlias[] | null | undefined) => {
  if (!Array.isArray(aliases) || aliases.length < 1) {
    return '—';
  }

  const aliasLabels = aliases
    .map((alias) => [normalizeValue(alias.firstname), normalizeValue(alias.lastname)].filter(Boolean).join(' '))
    .filter(Boolean);

  if (aliasLabels.length < 1) {
    return '—';
  }

  return aliasLabels.join(', ');
};

const formatMedicationFrequency = (medication: MedcallParticipantMedication) => {
  const frequency = normalizeValue(medication.frequency).toLowerCase();
  if (!frequency) {
    return '—';
  }

  if (frequency === 'custom_days') {
    const days = Array.isArray(medication.days_of_week) ? medication.days_of_week.filter(Boolean).join(', ') : '';
    const timesPerDay = normalizeValue(medication.times_per_day);
    if (days && timesPerDay) {
      return `${days} • ${timesPerDay}`;
    }

    if (days) {
      return days;
    }
  }

  if (frequency === 'everyday') {
    return normalizeValue(medication.times_per_day) || 'Every day';
  }

  if (frequency === 'everyotherday') {
    return 'Every other day';
  }

  return frequency.replace(/_/g, ' ');
};

const legacyMedicationSignature = (medication: MedcallParticipantMedication) => [
  medication.medicine_name,
  medication.strength,
  medication.route,
  medication.frequency,
  medication.times_per_day,
  medication.days_of_week,
  medication.prn,
]
  .map((value) => normalizeValue(
    Array.isArray(value) || (value && typeof value === 'object')
      ? JSON.stringify(value)
      : value
  ).replace(/\s+/g, ' ').toLowerCase())
  .join('|');

const legacyMedicationHasStopDate = (medication: MedcallParticipantMedication) => (
  Boolean(Number(medication.stop_date_status)) || Boolean(normalizeValue(medication.stop_date))
);

const getLegacyMedicationDisplayName = (medication: MedcallParticipantMedication) => {
  const medicineName = normalizeValue(medication.medicine_name);
  const detailName = normalizeValue(
    Array.isArray(medication.medicine_detail)
      ? medication.medicine_detail[0]?.name
      : ''
  );
  const doseOnlyPattern = /^\s*\d+(?:\.\d+)?\s*(?:mg|mcg|g|kg|ml|l|unit|units)?(?:\s*\/\s*(?:\d+(?:\.\d+)?\s*)?(?:mg|mcg|g|kg|ml|l|unit|units))?\s*$/i;
  return doseOnlyPattern.test(medicineName) && detailName
    ? detailName
    : medicineName || detailName || '—';
};

const getLegacyMedicationDose = (medication: MedcallParticipantMedication) => {
  const medicineName = normalizeValue(medication.medicine_name);
  const strength = normalizeValue(medication.strength);
  if (/^\d+(?:\.\d+)?$/.test(strength)) {
    const doseAtEnd = medicineName.match(/(\d+(?:\.\d+)?\s*(?:mg|mcg|g|kg|ml|mL|l|L|unit|units)(?:\s*\/\s*(?:\d+(?:\.\d+)?\s*)?(?:mg|mcg|g|kg|ml|mL|l|L|unit|units))?)\s*$/i);
    const doseNumber = doseAtEnd?.[1]?.match(/^\d+(?:\.\d+)?/);
    if (doseAtEnd?.[1] && doseNumber && Number(doseNumber[0]) === Number(strength)) {
      return doseAtEnd[1].replace(/\s+/g, ' ');
    }
  }
  return strength || medicineName || '—';
};

const getLegacyMedicationFrequency = (medication: MedcallParticipantMedication) => {
  const frequency = normalizeValue(medication.frequency).toLowerCase();
  if (frequency === 'everyotherday') {
    return 'QOD';
  }
  if (frequency === 'custom_days') {
    const days = Array.isArray(medication.days_of_week)
      ? medication.days_of_week.filter(Boolean).join(', ')
      : '';
    return [days, normalizeValue(medication.times_per_day)].filter(Boolean).join(' - ').toUpperCase() || '—';
  }
  if (frequency === 'everyday') {
    return normalizeValue(medication.times_per_day).toUpperCase() || '—';
  }
  return frequency.toUpperCase() || '—';
};

const MedicationColumn = ({ legacy, children }: { legacy: boolean; children: React.ReactNode }) => {
  if (legacy) return <div className="medcall2-legacy-medication-column">{children}</div>;
  return <>{children}</>;
};

const Medcall2MedicationTypeIcon = ({ injection }: { injection: boolean }) => injection ? (
  <svg aria-hidden="true" className="medcall2-legacy-medication__type-icon" fill="none" viewBox="0 0 28 28">
    <circle cx="14" cy="14" fill="currentColor" r="14" />
    <path d="M8.7478 19.2395L7 20.9873M8.7478 19.2395L9.84842 20.3481C10.1897 20.6919 10.743 20.6919 11.0843 20.3481L20.0429 11.5941M8.7478 19.2395L7.49735 17.98C7.15607 17.6362 7.15607 17.0789 7.49735 16.7352L9.29154 14.9337M20.2222 7.7847L18.2423 9.78045M20.2222 7.7847L19.4375 7M20.2222 7.7847L21 8.56247M18.2423 9.78045L20.0429 11.5941M18.2423 9.78045L16.3353 7.8596M12.8356 14.0069L11.5216 12.6929M11.5216 12.6929L14.1474 10.0563M11.5216 12.6929L9.29154 14.9337M16.3353 7.8596L15.4757 7M16.3353 7.8596L14.1474 10.0563M20.0429 11.5941L21 12.5512M10.5768 16.2189L9.29154 14.9337M15.4901 11.399L14.1474 10.0563" stroke="#FAF9FC" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
) : (
  <svg aria-hidden="true" className="medcall2-legacy-medication__type-icon" fill="none" viewBox="0 0 28 28">
    <circle cx="14" cy="14" fill="currentColor" r="14" />
    <path d="M11.1005 11.1005L8.20101 14C6.59966 15.6013 6.59966 18.1976 8.20101 19.799C9.80236 21.4003 12.3987 21.4003 14 19.799L16.8995 16.8995M11.1005 11.1005L14 8.20101C15.6013 6.59966 18.1976 6.59966 19.799 8.20101C21.4003 9.80236 21.4003 12.3987 19.799 14L16.8995 16.8995M11.1005 11.1005L16.8995 16.8995M10.15 15.75C9.50946 16.3905 9.50946 17.2198 10.15 17.8603" stroke="#FAF9FC" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
  </svg>
);

const Medcall2MedicationSettingsIcon = () => (
  <svg aria-hidden="true" className="medcall2-legacy-medication__settings-icon" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 14V10L20.662 9.583C20.4687 8.88533 20.1913 8.21374 19.836 7.583L21.9 4.929L19.071 2.1L16.418 4.164C15.7873 3.80865 15.1157 3.53128 14.418 3.338L14 0H10L9.583 3.338C8.88533 3.53128 8.21374 3.80865 7.583 4.164L4.929 2.1L2.1 4.929L4.164 7.582C3.80865 8.21274 3.53128 8.88433 3.338 9.582L0 10V14L3.338 14.417C3.53128 15.1147 3.80865 15.7863 4.164 16.417L2.1 19.071L4.929 21.9L7.582 19.837C8.21274 20.1923 8.88433 20.4697 9.582 20.663L10 24H14L14.417 20.662C15.1147 20.4687 15.7863 20.1913 16.417 19.836L19.071 21.9L21.9 19.071L19.837 16.418C20.1923 15.7873 20.4697 15.1157 20.663 14.418L24 14ZM12 16C11.2089 16 10.4355 15.7654 9.77772 15.3259C9.11992 14.8864 8.60723 14.2616 8.30448 13.5307C8.00173 12.7998 7.92252 11.9956 8.07686 11.2196C8.2312 10.4437 8.61216 9.73098 9.17157 9.17157C9.73098 8.61216 10.4437 8.2312 11.2196 8.07686C11.9956 7.92252 12.7998 8.00173 13.5307 8.30448C14.2616 8.60723 14.8864 9.11992 15.3259 9.77772C15.7654 10.4355 16 11.2089 16 12C16 13.0609 15.5786 14.0783 14.8284 14.8284C14.0783 15.5786 13.0609 16 12 16Z" />
  </svg>
);

const formatDateForInput = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const match = normalizedValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (!match) {
    return '';
  }

  return match[1];
};

const formatDateTimeForInput = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const match = normalizedValue.match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (!match) {
    return '';
  }

  return `${match[1]}T${match[2] || '00'}:${match[3] || '00'}`;
};

const formatImportanceLabel = (value: number | null | undefined) => {
  if (value === 2) {
    return 'High';
  }

  if (value === 1) {
    return 'Medium';
  }

  if (value === 0) {
    return 'Low';
  }

  return '—';
};

const getActiveBadgeStyle = (isActive: boolean): React.CSSProperties => {
  if (isActive) {
    return {
      borderColor: 'var(--semantic-success)',
      backgroundColor: 'var(--surface-panel-solid)',
      color: 'var(--semantic-success)',
    };
  }

  return {
    borderColor: 'var(--neutral-gray-40)',
    backgroundColor: 'var(--surface-muted)',
    color: 'var(--neutral-gray-100)',
  };
};

const warningBadgeStyle: React.CSSProperties = {
  backgroundColor: 'var(--semantic-warning-soft)',
  borderColor: 'var(--semantic-warning-border-soft)',
  color: 'var(--semantic-warning)',
};

const errorPanelStyle: React.CSSProperties = {
  backgroundColor: 'var(--semantic-error-soft)',
  borderColor: 'var(--semantic-error-border-soft)',
  color: 'var(--semantic-error)',
};

const getLosAngelesDateOnly = (value: Date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
  }).formatToParts(value);
  const getPart = (type: string) => parts.find((part) => part.type === type)?.value || '';
  return `${getPart('year')}-${getPart('month')}-${getPart('day')}`;
};

const getLosAngelesDateOnlyWithOffset = (dayOffset: number) => {
  const nextDate = new Date();
  nextDate.setUTCDate(nextDate.getUTCDate() + dayOffset);
  return getLosAngelesDateOnly(nextDate);
};

const isBlockedEmailDomain = (email: string, filterDomains: string[]) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail.includes('@')) {
    return false;
  }

  const domain = normalizedEmail.split('@')[1] || '';
  return filterDomains.includes(domain);
};

const buildProfileDraft = (participant: MedcallParticipantDetail): ProfileDraft => ({
  cid: normalizeValue(participant.cid),
  dob: normalizeValue(participant.dob),
  optionFacilityId: participant.option_facility_id ? `${participant.option_facility_id}` : '',
  program: normalizeValue(participant.program),
  provider: normalizeValue(participant.provider),
});

const buildPersonDraft = (person: MedcallPerson | null | undefined): PersonDraft => ({
  confirmEmail: normalizeValue(person?.email),
  email: normalizeValue(person?.email),
  firstname: normalizeValue(person?.firstname),
  lastname: normalizeValue(person?.lastname),
  title: normalizeValue(person?.title),
});

const buildStatusDraft = (): StatusDraft => ({
  activationBackDate: getLosAngelesDateOnly(),
  otherReason: '',
  reason: DEACTIVATION_OPTIONS[0],
});

const MEDICATION_EMPTY_DRAFT: MedicationDraft = {
  administrationNotes: '',
  customDays: [],
  dateDue: '',
  dateGiven: '',
  dateInjectionArrived: '',
  frequency: '',
  importanceLevel: '2',
  medicationExternalId: '',
  medicineDetail: '',
  medicineName: '',
  onSite: '0',
  prn: false,
  psychotropicMeds: false,
  refillDate: '',
  refillStatus: true,
  route: '',
  startDate: '',
  stopDate: '',
  stopDateEnabled: false,
  strength: '',
  timesPerDay: '',
};

const buildMedicationDraft = (medication?: MedcallParticipantMedication | null): MedicationDraft => {
  if (!medication) {
    return { ...MEDICATION_EMPTY_DRAFT };
  }

  const injectionInfo = Array.isArray(medication.injection_info) ? medication.injection_info[0] : null;
  const refillEntry = Array.isArray(medication.refills) ? medication.refills[0] : null;
  const route = normalizeValue(medication.route);
  const frequency = normalizeValue(medication.frequency).toLowerCase();
  const defaultTimesPerDay = route === 'IM-LAI' ? 'qday' : '';
  return {
    administrationNotes: normalizeValue(injectionInfo?.administration_notes),
    customDays: Array.isArray(medication.days_of_week) ? medication.days_of_week.filter(Boolean) : [],
    dateDue: formatDateForInput(injectionInfo?.date_due),
    dateGiven: formatDateForInput(injectionInfo?.date_given),
    dateInjectionArrived: formatDateForInput(injectionInfo?.date_injection_arrived),
    frequency,
    importanceLevel: Number.isInteger(Number(medication.importance_level))
      ? `${Number(medication.importance_level)}`
      : '2',
    medicationExternalId: resolveStoredMedicationExternalId({
      medicineDetail: medication.medicine_detail,
      uuid: medication.uuid,
    }),
    medicineDetail: JSON.stringify(Array.isArray(medication.medicine_detail) ? medication.medicine_detail : []),
    medicineName: normalizeValue(medication.medicine_name),
    onSite: Number.isInteger(Number(injectionInfo?.on_site)) ? `${Number(injectionInfo?.on_site)}` : '0',
    prn: Boolean(medication.prn),
    psychotropicMeds: Boolean(medication.psychotropic_meds),
    refillDate: formatDateForInput(refillEntry?.refill_date),
    refillStatus: Boolean(medication.refill_status),
    route,
    startDate: formatDateTimeForInput(medication.start_date),
    stopDate: formatDateTimeForInput(medication.stop_date),
    stopDateEnabled: normalizeValue(medication.stop_date_status) === '1',
    strength: normalizeValue(medication.strength),
    timesPerDay: normalizeValue(medication.times_per_day).toLowerCase() || defaultTimesPerDay,
  };
};

const participantProgramUsesFacility = (program: string) => !NURSELESS_PROGRAMS.includes(program);

const DetailField = ({ compact = false, label, value }: { compact?: boolean; label: string; value: React.ReactNode }) => {
  return (
    <div className={compact ? 'min-w-0 space-y-0.5' : 'space-y-1'}>
      <div className={`${compact ? 'text-xs' : 'text-sm tracking-wide'} truncate font-medium uppercase text-[var(--neutral-gray-90)]`}>{label}</div>
      <div
        className={`${compact ? 'truncate whitespace-nowrap' : ''} text-sm text-[var(--neutral-gray-120)]`}
        title={typeof value === 'string' ? value : undefined}
      >
        {value}
      </div>
    </div>
  );
};

const DialogField = ({
  children,
  label,
  required = false,
}: {
  children: React.ReactNode;
  label: string;
  required?: boolean;
}) => {
  return (
    <label className="space-y-2">
      <div className="text-sm font-medium text-[var(--neutral-gray-120)]">
        {label}
        {required ? <span className="ml-1 text-[var(--semantic-error)]">*</span> : null}
      </div>
      {children}
    </label>
  );
};

const getMedicationFrequencyOptions = (route: string) => {
  if (route !== 'IM-LAI') {
    return MEDICATION_FREQUENCY_OPTIONS;
  }

  return MEDICATION_FREQUENCY_OPTIONS.filter((option) => !IM_LAI_FILTERED_FREQUENCIES.has(option.value));
};

const getMedicationScheduleOptions = (frequency: string) => {
  const matchedOption = MEDICATION_FREQUENCY_OPTIONS.find((option) => option.value === frequency) || null;
  if (!matchedOption) {
    return [];
  }

  return matchedOption.scheduleOptions;
};

const formatMedicationScheduleLabel = (value: string) => MEDICATION_TIME_OPTION_LABELS[value] || value.toUpperCase();

const isMedicationScatteredErcProgram = (program: string) => NURSELESS_PROGRAMS.includes(program);

const isProjectSitesProgram = (program: string) => program === 'Project Sites';

const formatTimelineStatusLabel = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  if (!normalizedValue) {
    return 'Unset';
  }

  if (normalizedValue === 'notTaken') {
    return 'Not Taken';
  }

  if (normalizedValue === 'refilled') {
    return 'Refilled';
  }

  return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
};

const formatInjectionOnSiteLabel = (
  value: number | null | undefined,
  preserveLegacySemantics = false,
) => {
  if (preserveLegacySemantics) {
    return ['Not Yet On-site', 'N.A.', 'On-Site'][Number(value)] || '';
  }

  if (value === 2) {
    return 'Given';
  }

  if (value === 1) {
    return 'On Site';
  }

  if (value === 0) {
    return 'Pending';
  }

  return '—';
};

const formatTimeLabel = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const match = normalizedValue.match(/^(\d{2}):(\d{2})/);
  if (!match) {
    return normalizedValue || '—';
  }

  const hourValue = Number(match[1]);
  const minuteValue = match[2];
  const suffix = hourValue >= 12 ? 'PM' : 'AM';
  const normalizedHour = hourValue % 12 || 12;
  return `${normalizedHour}:${minuteValue} ${suffix}`;
};

const formatMedicationTimelineDoseLabel = (
  medication: MedcallParticipantMedication,
  timeline: MedcallMedicationTimeline
) => {
  const pieces = [
    normalizeValue(timeline.dose) ? `${normalizeValue(timeline.dose)} dose` : '',
    normalizeValue(medication.route) || normalizeValue(timeline.route),
    normalizeValue(medication.strength) || normalizeValue(timeline.strength),
  ].filter(Boolean);

  if (pieces.length < 1) {
    return formatMedicationFrequency(medication);
  }

  return pieces.join(' • ');
};

const parseUtcDateOnly = (value: string) => {
  const match = normalizeValue(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
};

const formatUtcDateOnly = (value: Date) => {
  const year = value.getUTCFullYear();
  const month = `${value.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${value.getUTCDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addUtcDays = (value: Date, dayOffset: number) => (
  new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate() + dayOffset))
);

const waitForLegacyTimelinePatch = () => new Promise<void>((resolve) => {
  window.setTimeout(resolve, 500);
});

const getUtcMonthStart = (value: Date) => (
  new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1))
);

const getUtcMonthEnd = (value: Date) => (
  new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + 1, 0))
);

const getUtcWeekStart = (value: Date, weekStart: number) => {
  const normalizedWeekStart = ((weekStart % 7) + 7) % 7;
  const currentDay = value.getUTCDay();
  const dayOffset = (currentDay - normalizedWeekStart + 7) % 7;
  return addUtcDays(value, -dayOffset);
};

const getUtcWeekEnd = (value: Date, weekStart: number) => addUtcDays(getUtcWeekStart(value, weekStart), 6);

const getProjectSitesMonthGridDates = (monthValue: string, weekStart: number) => {
  const selectedMonth = parseUtcDateOnly(`${monthValue}-01`);
  if (!selectedMonth) {
    return [];
  }

  const monthStart = getUtcMonthStart(selectedMonth);
  const monthEnd = getUtcMonthEnd(selectedMonth);
  const rangeStart = getUtcWeekStart(monthStart, weekStart);
  const rangeEnd = getUtcWeekEnd(monthEnd, weekStart);
  const dates: Date[] = [];
  let currentDate = rangeStart;

  while (currentDate.getTime() <= rangeEnd.getTime()) {
    dates.push(currentDate);
    currentDate = addUtcDays(currentDate, 1);
  }

  return dates;
};

const getProjectSitesMonthWeekRanges = (monthValue: string, weekStart: number) => {
  const selectedMonth = parseUtcDateOnly(`${monthValue}-01`);
  if (!selectedMonth) {
    return [];
  }

  const monthStart = getUtcMonthStart(selectedMonth);
  const monthEnd = getUtcMonthEnd(selectedMonth);
  const rangeStart = getUtcWeekStart(monthStart, weekStart);
  const weekRanges: Array<{ endDate: string; label: string; startDate: string }> = [];
  let currentDate = rangeStart;

  while (currentDate.getTime() <= monthEnd.getTime()) {
    const weekStartDate = currentDate;
    const weekEndDate = getUtcWeekEnd(currentDate, weekStart);
    weekRanges.push({
      endDate: formatUtcDateOnly(weekEndDate),
      label: `${formatDate(formatUtcDateOnly(weekStartDate))} - ${formatDate(formatUtcDateOnly(weekEndDate))}`,
      startDate: formatUtcDateOnly(weekStartDate),
    });
    currentDate = addUtcDays(weekEndDate, 1);
  }

  return weekRanges;
};

const getProjectSitesYearMonths = (monthValue: string) => {
  const selectedMonth = parseUtcDateOnly(`${monthValue}-01`);
  if (!selectedMonth) {
    return [];
  }

  return Array.from({ length: 12 }, (_, index) => {
    const monthDate = new Date(Date.UTC(selectedMonth.getUTCFullYear(), index, 1));
    const monthLabel = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      timeZone: 'UTC',
    }).format(monthDate);
    return {
      endDate: formatUtcDateOnly(getUtcMonthEnd(monthDate)),
      label: monthLabel,
      startDate: formatUtcDateOnly(monthDate),
    };
  });
};

const getProjectSitesStatusLabel = (
  statusLookup: Map<string, MedcallProjectSitesMedicationStatus>,
  startDate: string,
  endDate: string
) => {
  const start = parseUtcDateOnly(startDate);
  const end = parseUtcDateOnly(endDate);
  if (!start || !end) {
    return 'Not Taken';
  }

  let currentDate = start;
  while (currentDate.getTime() <= end.getTime()) {
    const currentStatus = statusLookup.get(formatUtcDateOnly(currentDate));
    if (currentStatus?.status !== 'taken') {
      return 'Not Taken';
    }

    currentDate = addUtcDays(currentDate, 1);
  }

  return 'Taken';
};

export const MedcallParticipantDetailPage = ({
  bootstrap,
  embedded = false,
  odrparticipant,
  routeBase = '/tools/medcall',
}: {
  bootstrap: MedcallBootstrapContext;
  embedded?: boolean;
  odrparticipant?: number;
  routeBase?: string;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { participantId } = useParams<{ participantId: string }>();
  const locationState = (location.state || {}) as RouteLocationState;
  const requestedRouteDialog = locationState.openDialog === 'medication' || locationState.openDialog === 'message'
    ? locationState.openDialog
    : null;
  const normalizedParticipantId = Number(participantId || 0);
  const normalizedOdrparticipant = Number(odrparticipant || 0);
  const isValidParticipantId = Number.isInteger(normalizedParticipantId) && normalizedParticipantId > 0;
  const isValidOdrparticipant = Number.isInteger(normalizedOdrparticipant) && normalizedOdrparticipant > 0;
  const hasValidParticipantLookup = isValidParticipantId || isValidOdrparticipant;
  const bootstrapBackPath = `${bootstrap.defaultLandingPath || ''}`.startsWith('/tools/medcall')
    ? `${routeBase}${`${bootstrap.defaultLandingPath || ''}`.slice('/tools/medcall'.length)}`
    : bootstrap.defaultLandingPath;
  const backPath = locationState.from || bootstrapBackPath || `${routeBase}/all-clients`;
  const isMedcall2Page = routeBase === '/tools/medcall2';
  const verifyCatalogLookup = !embedded && (routeBase === '/tools/medcall' || isMedcall2Page);
  const latestMedicationStrengthRequest = React.useRef(createMedcallLatestRequestGuard()).current;

  const { data: legacyOptionData } = useQuery<{ medcallOptions: Medcall2OptionRow[] }>(MEDCALL2_PROFILE_OPTIONS, {
    fetchPolicy: 'network-only',
    skip: !isMedcall2Page || !hasValidParticipantLookup,
  });

  const { data: countyHolidayData } = useQuery<MedcallCountyHolidaysData>(MEDCALL_COUNTY_HOLIDAYS, {
    fetchPolicy: 'cache-first',
    skip: !isMedcall2Page,
  });

  const { data, error, loading, refetch } = useQuery<MedcallParticipantDetailData>(MEDCALL_PARTICIPANT_DETAIL_PAGE, {
    fetchPolicy: 'cache-and-network',
    skip: !hasValidParticipantLookup,
    variables: {
      includeTimelines: !embedded,
      odrparticipant: isValidOdrparticipant ? normalizedOdrparticipant : null,
      participantId: isValidParticipantId ? normalizedParticipantId : null,
      preserveLegacyMedicationOrder: isMedcall2Page,
    },
  });

  const [addToMyClients] = useMutation(MEDCALL_ADD_TO_MY_CLIENTS);
  const [removeFromMyClients] = useMutation(MEDCALL_REMOVE_FROM_MY_CLIENTS);
  const [updateParticipantProfile] = useMutation(MEDCALL_UPDATE_PARTICIPANT_PROFILE);
  const [updateParticipantStaff] = useMutation(MEDCALL_UPDATE_PARTICIPANT_STAFF);
  const [updateParticipantNurse] = useMutation(MEDCALL_UPDATE_PARTICIPANT_NURSE);
  const [updateParticipantPsychiatrist] = useMutation(MEDCALL_UPDATE_PARTICIPANT_PSYCHIATRIST);
  const [addParticipantNote] = useMutation(MEDCALL_ADD_PARTICIPANT_NOTE);
  const [addParticipantAlias] = useMutation(MEDCALL_ADD_PARTICIPANT_ALIAS);
  const [deleteParticipantNote] = useMutation(MEDCALL_DELETE_PARTICIPANT_NOTE);
  const [setParticipantActive] = useMutation(MEDCALL_SET_PARTICIPANT_ACTIVE);
  const [createMedication] = useMutation(MEDCALL_CREATE_MEDICATION);
  const [updateMedication] = useMutation(MEDCALL_UPDATE_MEDICATION);
  const [updateImlaiDueDate] = useMutation(MEDCALL_UPDATE_IMLAI_DUE_DATE);
  const [setMedicationActive] = useMutation(MEDCALL_SET_MEDICATION_ACTIVE);
  const [deleteMedication] = useMutation(MEDCALL_DELETE_MEDICATION);
  const [updateMedicationTimeline] = useMutation(MEDCALL_UPDATE_MEDICATION_TIMELINE, {
    fetchPolicy: isMedcall2Page ? 'no-cache' : undefined,
  });
  const [updateMedicationTimelineSite] = useMutation(MEDCALL_UPDATE_MEDICATION_TIMELINE_SITE, {
    fetchPolicy: isMedcall2Page ? 'no-cache' : undefined,
  });
  const [updateMedicationAdministrationNotes] = useMutation(MEDCALL_UPDATE_MEDICATION_ADMINISTRATION_NOTES, {
    fetchPolicy: isMedcall2Page ? 'no-cache' : undefined,
  });
  const [updateProjectSitesMedicationStatuses] = useMutation(MEDCALL_UPDATE_PROJECT_SITES_MEDICATION_STATUSES);
  const [updateProjectSitesClientOptions] = useMutation(MEDCALL_UPDATE_PROJECT_SITES_CLIENT_OPTIONS);
  const [loadMedicationTimelines] = useLazyQuery(MEDCALL_MEDICATION_TIMELINES, {
    fetchPolicy: 'network-only',
  });
  const [searchMedicationCatalog] = useLazyQuery<MedcallMedicationSearchData>(isMedcall2Page ? MEDCALL2_SEARCH_MEDICATIONS : MEDCALL_SEARCH_MEDICATIONS, {
    fetchPolicy: 'network-only',
  });
  const [loadMedicationStrengths] = useLazyQuery<MedcallMedicationStrengthsData>(isMedcall2Page ? MEDCALL2_MEDICATION_STRENGTHS : MEDCALL_MEDICATION_STRENGTHS, {
    fetchPolicy: 'network-only',
  });

  const [pendingAction, setPendingAction] = React.useState<string | null>(null);
  const [personAssignmentSubmitting, setPersonAssignmentSubmitting] = React.useState(false);
  const [activeDialog, setActiveDialog] = React.useState<'aliases' | 'edit' | 'med-list' | 'medication' | 'message' | 'nurse' | 'notes' | 'psychiatrist' | 'report' | 'staff' | 'status' | null>(requestedRouteDialog);
  const [medicationView, setMedicationView] = React.useState<MedcallMedicationView>('active');
  const [openMedicationMenuId, setOpenMedicationMenuId] = React.useState<number | null>(null);
  const [expandedMedicationIds, setExpandedMedicationIds] = React.useState<Set<number>>(() => new Set());
  const [medicationTimelineHistory, setMedicationTimelineHistory] = React.useState<Map<number, MedcallMedicationTimeline[]>>(() => new Map());
  const [medicationTimelinePages, setMedicationTimelinePages] = React.useState<Map<number, number>>(() => new Map());
  const [medicationTimelineOverrides, setMedicationTimelineOverrides] = React.useState<Map<number, Partial<MedcallMedicationTimeline>>>(() => new Map());
  const [projectSitesTrackerView, setProjectSitesTrackerView] = React.useState<ProjectSitesTrackerView>('day');
  const [projectSitesTrackerMonth, setProjectSitesTrackerMonth] = React.useState(() => getLosAngelesDateOnly().slice(0, 7));
  const [projectSitesTrackerSelection, setProjectSitesTrackerSelection] = React.useState<ProjectSitesTrackerSelection | null>(null);
  const [projectSitesTrackerStatus, setProjectSitesTrackerStatus] = React.useState<'notTaken' | 'taken'>('taken');
  const [projectSitesTrackerNotes, setProjectSitesTrackerNotes] = React.useState('');
  const [projectSitesWeekStartDraft, setProjectSitesWeekStartDraft] = React.useState('1');
  const [staffDialogTab, setStaffDialogTab] = React.useState('form');
  const [profileDraft, setProfileDraft] = React.useState<ProfileDraft>({
    cid: '',
    dob: '',
    optionFacilityId: '',
    program: '',
    provider: '',
  });
  const [staffDraft, setStaffDraft] = React.useState('');
  const [nurseDraft, setNurseDraft] = React.useState<PersonDraft>({
    confirmEmail: '',
    email: '',
    firstname: '',
    lastname: '',
    title: '',
  });
  const [psychiatristDraft, setPsychiatristDraft] = React.useState<PersonDraft>({
    confirmEmail: '',
    email: '',
    firstname: '',
    lastname: '',
    title: '',
  });
  const [noteDraft, setNoteDraft] = React.useState('');
  const [aliasDraft, setAliasDraft] = React.useState({ firstname: '', lastname: '' });
  const [deleteNoteTarget, setDeleteNoteTarget] = React.useState<MedcallParticipantNote | null>(null);
  const [statusDraft, setStatusDraft] = React.useState<StatusDraft>(buildStatusDraft());
  const [editingMedicationId, setEditingMedicationId] = React.useState<number | null>(null);
  const [medicationDraft, setMedicationDraft] = React.useState<MedicationDraft>({ ...MEDICATION_EMPTY_DRAFT });
  const [medicationPreviewVariables, setMedicationPreviewVariables] = React.useState<MedicationSaveVariables | null>(null);
  const [medicationSearchResults, setMedicationSearchResults] = React.useState<MedcallMedicationSearchItem[]>([]);
  const [medicationStrengthOptions, setMedicationStrengthOptions] = React.useState<MedcallMedicationStrengthOption[]>([]);
  const [medicationSearchLoading, setMedicationSearchLoading] = React.useState(false);
  const [usesCustomMedicationStrength, setUsesCustomMedicationStrength] = React.useState(false);
  const [legacyMedicationSelected, setLegacyMedicationSelected] = React.useState(false);
  const [dismissedImlaiWarningParticipantId, setDismissedImlaiWarningParticipantId] = React.useState(0);
  const routeDialogHandledRef = React.useRef('');
  const medicationPreviewSubmittingRef = React.useRef(false);
  const activeParticipantId = Number(data?.medcallParticipantDetail?.id || 0);
  const activeParticipantWeekStart = Number.isInteger(Number(data?.medcallParticipantDetail?.project_sites_client_options?.week_start))
    ? Number(data?.medcallParticipantDetail?.project_sites_client_options?.week_start)
    : 1;

  React.useEffect(() => {
    setProjectSitesWeekStartDraft(`${activeParticipantWeekStart}`);
  }, [activeParticipantId, activeParticipantWeekStart]);

  React.useEffect(() => {
    setDismissedImlaiWarningParticipantId(0);
  }, [activeParticipantId]);

  React.useEffect(() => {
    setMedicationView('active');
    setOpenMedicationMenuId(null);
    setExpandedMedicationIds(new Set());
    setMedicationTimelineHistory(new Map());
    setMedicationTimelinePages(new Map());
    setMedicationTimelineOverrides(new Map());
    setProjectSitesTrackerSelection(null);
    setProjectSitesTrackerStatus('taken');
    setProjectSitesTrackerNotes('');
  }, [activeParticipantId, isMedcall2Page]);

  React.useEffect(() => {
    if (!requestedRouteDialog || activeParticipantId < 1) {
      return;
    }

    const requestKey = `${activeParticipantId}:${requestedRouteDialog}`;
    if (routeDialogHandledRef.current === requestKey) {
      return;
    }

    routeDialogHandledRef.current = requestKey;
    if (requestedRouteDialog === 'medication') {
      const participantProgram = normalizeValue(data?.medcallParticipantDetail?.program);
      setEditingMedicationId(null);
      setMedicationSearchResults([]);
      setMedicationStrengthOptions([]);
      setUsesCustomMedicationStrength(false);
      setLegacyMedicationSelected(false);
      setMedicationDraft({
        ...MEDICATION_EMPTY_DRAFT,
        refillStatus: !isMedicationScatteredErcProgram(participantProgram),
      });
    }

    setActiveDialog(requestedRouteDialog);
    navigate(location.pathname, {
      replace: true,
      state: {
        from: locationState.from,
      },
    });
  }, [
    activeParticipantId,
    data?.medcallParticipantDetail?.program,
    location.pathname,
    locationState.from,
    navigate,
    requestedRouteDialog,
  ]);

  React.useEffect(() => {
    if (activeDialog !== 'medication') {
      return;
    }

    const searchText = normalizeValue(medicationDraft.medicineName);
    if (searchText.length < 4 || medicationDraft.medicationExternalId) {
      setMedicationSearchResults([]);
      setMedicationSearchLoading(false);
      return;
    }

    let isCurrentRequest = true;
    const timeoutId = window.setTimeout(async () => {
      setMedicationSearchLoading(true);
      try {
        const result = await searchMedicationCatalog({ variables: { searchText } });
        if (!isCurrentRequest) {
          return;
        }

        const searchItems = result.data?.medcallSearchMedications;
        if (verifyCatalogLookup && (result.error || !Array.isArray(searchItems))) {
          throw new Error('Unable to search medications.');
        }
        setMedicationSearchResults(
          Array.isArray(searchItems)
            ? searchItems.filter(Boolean)
            : []
        );
      } catch (searchError: unknown) {
        if (!isCurrentRequest) {
          return;
        }

        setMedicationSearchResults([]);
        toast.error(verifyCatalogLookup ? 'Unable to search medications.' : (searchError as { message?: string } | null)?.message || 'Unable to search medications.');
      }

      if (isCurrentRequest) {
        setMedicationSearchLoading(false);
      }
    }, 300);

    return () => {
      isCurrentRequest = false;
      window.clearTimeout(timeoutId);
    };
  }, [activeDialog, medicationDraft.medicationExternalId, medicationDraft.medicineName, searchMedicationCatalog, verifyCatalogLookup]);

  const toggleMedicationHistory = React.useCallback(async (medicationId: number) => {
    if (embedded && !medicationTimelineHistory.has(medicationId)) {
      try {
        const result = await loadMedicationTimelines({ variables: { medicationId } });
        const timelines = Array.isArray(result.data?.medcallMedicationTimelines)
          ? result.data.medcallMedicationTimelines
          : [];
        setMedicationTimelineHistory((current) => new Map(current).set(medicationId, timelines));
      } catch (timelineError: any) {
        toast.error(timelineError?.message || 'Unable to load medication history.');
        return;
      }
    }

    setExpandedMedicationIds((previousIds) => {
      const nextIds = new Set(previousIds);
      if (nextIds.has(medicationId)) {
        nextIds.delete(medicationId);
        return nextIds;
      }

      nextIds.add(medicationId);
      return nextIds;
    });
  }, [embedded, loadMedicationTimelines, medicationTimelineHistory]);

  if (!hasValidParticipantLookup) {
    return (
      <Card>
        <CardContent className="p-[var(--panel-padding)]">
          <div className="text-sm text-[var(--neutral-gray-100)]">Client not found.</div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return <OdrLoadingSpinner />;
  }

  if (error) {
    return (
      <Card style={errorPanelStyle}>
        <CardContent className="p-[var(--panel-padding)]">
          <div className="text-sm">Unable to load client details. Try again.</div>
        </CardContent>
      </Card>
    );
  }

  const participant = data?.medcallParticipantDetail;
  if (!embedded && isValidParticipantId && participant && participant.id !== normalizedParticipantId) {
    return (
      <Card style={errorPanelStyle}>
        <CardContent className="p-[var(--panel-padding)]">
          <div className="text-sm">The requested client details could not be loaded.</div>
        </CardContent>
      </Card>
    );
  }
  if (!participant) {
    return (
      <Card>
        <CardContent className="p-[var(--panel-padding)]">
          <div className="text-sm text-[var(--neutral-gray-100)]">Client not found.</div>
        </CardContent>
      </Card>
    );
  }

  const actionMeta = data?.medcallParticipantActionMeta;
  const legacyOptions = readMedcall2IntakeOptions(legacyOptionData?.medcallOptions);
  const facilityOptions: MedcallListOption[] = isMedcall2Page
    ? legacyOptions.facilityOptions
    : Array.isArray(actionMeta?.facilityOptions) ? actionMeta?.facilityOptions || [] : [];
  const filterDomains: string[] = isMedcall2Page ? legacyOptions.filterDomains : Array.isArray(actionMeta?.filterDomains)
    ? (actionMeta?.filterDomains || []).map((value) => normalizeValue(value).toLowerCase()).filter(Boolean)
    : [];
  const programOptions: MedcallListOption[] = isMedcall2Page
    ? legacyOptions.programOptions.map((option) => ({ label: option.label, value: option.label }))
    : Array.isArray(actionMeta?.programOptions) ? actionMeta?.programOptions || [] : [];
  const providerOptions = (() => {
    if (isMedcall2Page) return readMedcall2ProviderOptions(legacyOptionData?.medcallOptions);
    const seen = new Set<string>();
    const options: Array<{ label: string; value: string }> = [];
    const pushOption = (labelValue: string) => {
      const normalized = normalizeValue(labelValue);
      if (!normalized || seen.has(normalized)) {
        return;
      }

      seen.add(normalized);
      options.push({
        label: normalized,
        value: normalized,
      });
    };

    const metaOptions: MedcallListOption[] = Array.isArray(actionMeta?.providerOptions)
      ? actionMeta?.providerOptions || []
      : [];
    metaOptions.forEach((option) => {
      pushOption(normalizeValue(option.value) || normalizeValue(option.label));
    });
    pushOption(participant.provider || '');
    return options;
  })();
  const staffOptions = (() => {
    const seen = new Set<number>();
    const options: MedcallStaffOption[] = [];
    const pushOption = (option: MedcallStaffOption | null) => {
      if (!option) {
        return;
      }

      const optionId = Number(option.id || 0);
      if (!Number.isInteger(optionId) || optionId < 1 || seen.has(optionId)) {
        return;
      }

      seen.add(optionId);
      options.push({
        email: normalizeValue(option.email) || null,
        fullname: normalizeValue(option.fullname) || null,
        id: optionId,
        username: normalizeValue(option.username) || null,
      });
    };

    const metaOptions: MedcallStaffOption[] = Array.isArray(actionMeta?.staffOptions)
      ? actionMeta?.staffOptions || []
      : [];
    metaOptions.forEach((option) => pushOption(option));

    if (!isMedcall2Page && participant.user_id && participant.user_id > 0) {
      pushOption({
        email: null,
        fullname: normalizeValue(participant.staff_assigned) || `Staff ${participant.user_id}`,
        id: participant.user_id,
        username: null,
      });
    }

    return orderMedcallStaffOptions(options, routeBase);
  })();

  const isOnMyClients = Array.isArray(participant.view_user_ids)
    ? participant.view_user_ids.includes(`${bootstrap.medcallUserId}`)
    : false;
  const medications = Array.isArray(participant.medications) ? participant.medications : [];
  const configuredCountyHolidays = countyHolidayData?.medcallCountyHolidays;
  const countyHolidays = Array.isArray(configuredCountyHolidays)
    ? configuredCountyHolidays.filter(Boolean) as Array<{
        date?: string | null;
        holiday?: string | null;
      }>
    : [];
  const projectSitesMedicationStatuses = Array.isArray(participant.project_sites_medication_statuses)
    ? participant.project_sites_medication_statuses
    : [];
  const projectSitesClientOptions = participant.project_sites_client_options || null;
  const notes = Array.isArray(participant.notes) ? participant.notes : [];
  const activationHistory = Array.isArray(participant.activation_history) ? participant.activation_history : [];
  const staffHistory = Array.isArray(participant.history_ids) ? participant.history_ids : [];
  const hasFullAccess = Boolean(bootstrap.hasSuperAccess || bootstrap.isAdminUser);
  const participantProgram = normalizeValue(participant.program);
  const isManagedProgram = PROGRAM_LEVEL_MANAGEMENT_PROGRAMS.includes(participantProgram);
  const isNurselessProgram = NURSELESS_PROGRAMS.includes(participantProgram);
  const isProjectSitesClient = isProjectSitesProgram(participantProgram);
  const isMedicationReadOnlyProgram = isMedicationScatteredErcProgram(participantProgram);
  const canManageParticipant = hasFullAccess || isManagedProgram;
  const canManageMedications = canManageParticipant;
  const canEditMedicationTimelineStatus = userCanEditMedcallTimelineStatus(
    routeBase,
    Boolean(bootstrap.hasMedcallPowerAccess),
    canManageMedications,
  );
  const canEditMedicationTimelineWitness = userCanEditMedcallTimelineWitness(
    routeBase,
    canManageMedications,
  );
  const canEditMedicationTimelineSite = userCanEditMedcallTimelineSite(
    routeBase,
    Boolean(bootstrap.hasMedcallPowerAccess),
    canManageMedications,
  );
  const profileProgramUsesFacility = participantProgramUsesFacility(getMedcallProfileFacilityProgram(
    routeBase,
    participantProgram,
    normalizeValue(profileDraft.program),
  ));
  const showAliasEntryFields = shouldShowMedcallAliasEntryFields(routeBase, hasFullAccess);
  const identityDialogCopy = getMedcallIdentityDialogCopy(routeBase, pendingAction);
  const frequencyOptions = getMedicationFrequencyOptions(medicationDraft.route);
  const scheduleOptions = getMedicationScheduleOptions(medicationDraft.frequency);
  const medicationFieldsPending = !embedded && (routeBase === '/tools/medcall' || isMedcall2Page) && pendingAction !== null;
  const projectSitesWeekStart = Number.isInteger(Number(projectSitesClientOptions?.week_start))
    ? Number(projectSitesClientOptions?.week_start)
    : 1;
  const projectSitesStatusLookup = new Map(
    projectSitesMedicationStatuses
      .map((status) => {
        const dateKey = normalizeValue(status.date);
        if (!dateKey) {
          return null;
        }

        return [dateKey, status] as const;
      })
      .filter(Boolean) as Array<readonly [string, MedcallProjectSitesMedicationStatus]>
  );
  const medicationConfirmationRows = isMedcall2Page && medicationPreviewVariables
    ? buildLegacyMedcallMedicationConfirmationRows(medicationDraft)
    : [];
  const showLegacyMedicationSearchOnly = isMedcall2Page
    && !editingMedicationId
    && !legacyMedicationSelected;
  const showLegacyMedicationDoseOnly = isMedcall2Page
    && !showLegacyMedicationSearchOnly
    && !normalizeValue(medicationDraft.strength);
  const activeLegacyMedicationSignatures = new Set(
    medications
      .filter((medication) => Boolean(medication.active))
      .map(legacyMedicationSignature)
  );
  const legacyNonPrnMedications = medications
    .filter((medication) => !(Boolean(medication.prn) && Boolean(medication.active)))
    .filter((medication) => !(
      !Boolean(medication.active)
      && !legacyMedicationHasStopDate(medication)
      && activeLegacyMedicationSignatures.has(legacyMedicationSignature(medication))
    ))
    .sort((left, right) => Number(Boolean(left.prn)) - Number(Boolean(right.prn)))
    .sort((left, right) => Number(Boolean(right.active)) - Number(Boolean(left.active)));
  const legacyPrnMedications = medications.filter((medication) => (
    Boolean(medication.prn) && Boolean(medication.active)
  ));
  const visibleLegacyNonPrnMedications = medicationView === 'historical'
    ? legacyNonPrnMedications.filter((medication) => !Boolean(medication.active))
    : medicationView === 'active'
      ? legacyNonPrnMedications.filter((medication) => Boolean(medication.active))
      : legacyNonPrnMedications;
  const filteredMedications = (() => {
    if (isMedcall2Page) {
      return [...visibleLegacyNonPrnMedications, ...legacyPrnMedications];
    }

    if (medicationView === 'prn') {
      return medications.filter((medication) => Boolean(medication.prn) && Boolean(medication.active));
    }

    if (medicationView === 'historical') {
      return medications.filter((medication) => !Boolean(medication.prn) && !Boolean(medication.active));
    }

    return medications.filter((medication) => !Boolean(medication.prn) && Boolean(medication.active));
  })();
  const projectSitesDayGridDates = getProjectSitesMonthGridDates(projectSitesTrackerMonth, projectSitesWeekStart);
  const projectSitesWeekRanges = getProjectSitesMonthWeekRanges(projectSitesTrackerMonth, projectSitesWeekStart);
  const projectSitesMonthRanges = getProjectSitesYearMonths(projectSitesTrackerMonth);
  const projectSitesWeekdayLabels = (() => {
    const monthDate = parseUtcDateOnly(`${projectSitesTrackerMonth}-01`) || parseUtcDateOnly(`${getLosAngelesDateOnly().slice(0, 7)}-01`);
    if (!monthDate) {
      return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    }

    const weekStartDate = getUtcWeekStart(monthDate, projectSitesWeekStart);
    return Array.from({ length: 7 }, (_, index) => {
      const dayDate = addUtcDays(weekStartDate, index);
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        weekday: 'short',
      }).format(dayDate);
    });
  })();
  const availableStaffOptions = hasFullAccess
    ? staffOptions
    : staffOptions.filter((option) => option.id === participant.user_id || option.id === bootstrap.medcallUserId);
  const minDeactivationDate = getLosAngelesDateOnlyWithOffset(-7);
  const maxDeactivationDate = getLosAngelesDateOnly();

  const loadStrengthOptionsForMedication = async (
    externalId: string,
    medicineName = '',
    sortForEdit = false
  ) => {
    const requestId = latestMedicationStrengthRequest.begin();
    const normalizedExternalId = normalizeValue(externalId);
    setMedicationStrengthOptions([]);
    setUsesCustomMedicationStrength(false);
    if (!normalizedExternalId) {
      return;
    }

    try {
      const result = await loadMedicationStrengths({
        variables: {
          externalId: normalizedExternalId,
          medicineName: normalizeValue(medicineName) || null,
        },
      });
      if (verifyCatalogLookup && !latestMedicationStrengthRequest.isLatest(requestId)) return;
      const strengthOptions = result.data?.medcallMedicationStrengths;
      if (verifyCatalogLookup && (result.error || !Array.isArray(strengthOptions))) {
        throw new Error('Unable to load medication doses.');
      }
      const availableOptions = Array.isArray(strengthOptions)
        ? strengthOptions.filter((option) => Boolean(getMedcallStrengthOptionValue(option)))
        : [];
      setMedicationStrengthOptions(
        sortForEdit ? sortMedcallStrengthOptionsNumerically(availableOptions) : availableOptions
      );
    } catch (strengthError: unknown) {
      if (verifyCatalogLookup && !latestMedicationStrengthRequest.isLatest(requestId)) return;
      toast.error(verifyCatalogLookup ? 'Unable to load medication doses.' : (strengthError as { message?: string } | null)?.message || 'Unable to load medication doses.');
    }
  };

  const handleMedicationCatalogSelection = async (item: MedcallMedicationSearchItem) => {
    const medicineName = normalizeValue(item.name || item.displayName);
    const externalId = normalizeValue(item.externalId || item.drugbankPcid || item.uuid);
    if (!medicineName) {
      return;
    }

    setLegacyMedicationSelected(true);
    setMedicationDraft((current) => ({
      ...current,
      medicationExternalId: externalId,
      medicineDetail: normalizeValue(item.medicineDetail),
      medicineName,
      strength: '',
    }));
    setMedicationSearchResults([]);
    await loadStrengthOptionsForMedication(externalId, medicineName);
  };

  const openEditDialog = () => {
    setProfileDraft(buildProfileDraft(participant));
    setActiveDialog('edit');
  };

  const openStaffDialog = () => {
    setStaffDraft(participant.user_id ? `${participant.user_id}` : '');
    setStaffDialogTab('form');
    setActiveDialog('staff');
  };

  const openNurseDialog = () => {
    setNurseDraft(buildPersonDraft(participant.nurse_name));
    setActiveDialog('nurse');
  };

  const openPsychiatristDialog = () => {
    setPsychiatristDraft(buildPersonDraft(participant.psychiatrist));
    setActiveDialog('psychiatrist');
  };

  const openNotesDialog = () => {
    setNoteDraft('');
    setActiveDialog('notes');
  };

  const openAliasesDialog = () => {
    setAliasDraft({ firstname: '', lastname: '' });
    setActiveDialog('aliases');
  };

  const openMessageDialog = () => {
    setActiveDialog('message');
  };

  const openCreateMedicationDialog = () => {
    if (verifyCatalogLookup) latestMedicationStrengthRequest.invalidate();
    setEditingMedicationId(null);
    setMedicationPreviewVariables(null);
    setMedicationSearchResults([]);
    setMedicationStrengthOptions([]);
    setUsesCustomMedicationStrength(false);
    setLegacyMedicationSelected(false);
    setMedicationDraft({
      ...MEDICATION_EMPTY_DRAFT,
      refillStatus: !isMedicationReadOnlyProgram,
    });
    setActiveDialog('medication');
  };

  const openEditMedicationDialog = (medication: MedcallParticipantMedication) => {
    setEditingMedicationId(medication.id);
    setMedicationPreviewVariables(null);
    const nextDraft = buildMedicationDraft(medication);
    setMedicationSearchResults([]);
    setMedicationStrengthOptions([]);
    setUsesCustomMedicationStrength(false);
    setLegacyMedicationSelected(true);
    setMedicationDraft(nextDraft);
    setActiveDialog('medication');
    void loadStrengthOptionsForMedication(nextDraft.medicationExternalId, nextDraft.medicineName, true);
  };

  const openStatusDialog = () => {
    if (isMedcall2Page && !participant.active) {
      void handleSaveStatus();
      return;
    }
    setStatusDraft(buildStatusDraft());
    setActiveDialog('status');
  };

  const closeDialog = () => {
    if (verifyCatalogLookup) latestMedicationStrengthRequest.invalidate();
    setActiveDialog(null);
    setEditingMedicationId(null);
    setMedicationPreviewVariables(null);
    setDeleteNoteTarget(null);
    setMedicationSearchResults([]);
    setMedicationStrengthOptions([]);
    setMedicationSearchLoading(false);
    setUsesCustomMedicationStrength(false);
    setLegacyMedicationSelected(false);
  };

  const handleMutationError = (mutationError: any, fallbackMessage: string) => {
    toast.error(mutationError?.message || fallbackMessage);
  };

  const didConfirmMedicationTimelineMutation = (
    result: { data?: Record<string, { id?: number } | null> | null; errors?: readonly unknown[] },
    fieldName: string,
    timelineId: number,
    allowLegacyNull = false,
  ) => {
    const updatedTimeline = result.data?.[fieldName];
    if (result.errors?.length) return false;
    if (updatedTimeline?.id === timelineId) return true;

    return isMedcall2Page && allowLegacyNull && updatedTimeline === null && didMedcall2TimelineMutationComplete(
      routeBase, result.data, fieldName,
    );
  };

  const didConfirmImlaiDueDate = (
    result: { data?: { medcallUpdateImlaiDueDate?: { id?: number; injection_info?: Array<{ date_due?: string | null }> | null } | null } | null; errors?: readonly unknown[] },
    update: MedcallImlaiDueDateUpdate,
  ) => !result.errors?.length
    && result.data?.medcallUpdateImlaiDueDate?.id === update.medicationId
    && normalizeMedcallImlaiDate(result.data.medcallUpdateImlaiDueDate.injection_info?.[0]?.date_due) === update.dueDate;

  const handleUpdateImlaiDueDate = async (update: MedcallImlaiDueDateUpdate) => {
    if (pendingAction !== null) return;
    setPendingAction(`imlai-due-date-${update.medicationId}`);
    try {
      const result = await updateImlaiDueDate({ variables: { dueDate: update.dueDate, medicationId: update.medicationId } });
      if (!didConfirmImlaiDueDate(result, update)) {
        toast.error('Unable to update medication due date.');
        return;
      }

      toast.success('Medication due date updated.');
      try {
        await refetch();
      } catch {
        toast.error('The medication due date update was saved, but client details could not be refreshed. Reopen the client.');
      }
    } catch {
      toast.error('Unable to update medication due date.');
    } finally {
      setPendingAction(null);
    }
  };

  const handleUpdateAllImlaiDueDates = async (updates: MedcallImlaiDueDateUpdate[]) => {
    if (pendingAction !== null || updates.length < 1) return;
    setPendingAction('imlai-due-date-all');
    try {
      const results = await Promise.allSettled(updates.map((update) => updateImlaiDueDate({
        variables: { dueDate: update.dueDate, medicationId: update.medicationId },
      })));
      const everyUpdateSucceeded = results.every((result, index) => result.status === 'fulfilled'
        && didConfirmImlaiDueDate(result.value, updates[index]));
      if (everyUpdateSucceeded) toast.success('Medication due dates updated.');
      if (!everyUpdateSucceeded) toast.error('Unable to update every medication due date.');

      try {
        await refetch();
      } catch {
        toast.error(everyUpdateSucceeded
          ? 'The medication due date update was saved, but client details could not be refreshed. Reopen the client.'
          : 'Unable to refresh medication details. Reopen the client.');
      }
    } catch {
      toast.error('Unable to update every medication due date.');
    } finally {
      setPendingAction(null);
    }
  };

  const handleAddToMyClients = async () => {
    if (pendingAction !== null) return;
    setPendingAction('add-list');
    try {
      const result = await addToMyClients({ variables: { participantId: activeParticipantId } });
      const response = result.data?.medcallAddToMyClients;
      if (result.errors?.length || response?.responseCode !== 200 || response.responseID !== activeParticipantId) {
        toast.error('Unable to add client');
        return;
      }

      toast.success(response.message || 'Client added');
      try {
        await refetch();
      } catch {
        toast.error('The client list was updated, but client details could not be refreshed. Reopen the client.');
      }
    } catch {
      toast.error('Unable to add client');
    } finally {
      setPendingAction(null);
    }
  };

  const handleRemoveFromMyClients = async () => {
    if (pendingAction !== null) return;
    setPendingAction('remove-list');
    try {
      const result = await removeFromMyClients({ variables: { participantId: activeParticipantId } });
      const response = result.data?.medcallRemoveFromMyClients;
      if (result.errors?.length || response?.responseCode !== 200 || response.responseID !== activeParticipantId) {
        toast.error('Unable to remove client');
        return;
      }

      toast.success(response.message || 'Client removed');
      try {
        await refetch();
      } catch {
        toast.error('The client list was updated, but client details could not be refreshed. Reopen the client.');
      }
    } catch {
      toast.error('Unable to remove client');
    } finally {
      setPendingAction(null);
    }
  };

  const saveParticipantChange = async ({
    action, mutate, resultField, failureMessage, successMessage,
    refreshFailureMessage = 'The client update was saved, but client details could not be refreshed. Reopen the client.',
  }: {
    action: 'profile' | 'staff' | 'alias' | 'status' | 'nurse' | 'psychiatrist';
    mutate: () => Promise<{ data?: Record<string, { id?: number } | null> | null; errors?: readonly unknown[] }>;
    resultField: string;
    failureMessage: string;
    successMessage?: string;
    refreshFailureMessage?: string;
  }) => {
    if (pendingAction !== null) return;
    setPendingAction(action);
    try {
      const result = await mutate();
      if (result.errors?.length || result.data?.[resultField]?.id !== activeParticipantId) {
        toast.error(failureMessage);
        return;
      }
      if (successMessage) toast.success(successMessage);
      try {
        await refetch();
      } catch {
        toast.error(refreshFailureMessage);
      }
      closeDialog();
    } catch {
      toast.error(failureMessage);
    } finally {
      setPendingAction(null);
    }
  };

  const handleSaveProfile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextProgram = normalizeValue(profileDraft.program) || participantProgram;
    const nextProvider = normalizeValue(profileDraft.provider);
    const nextDob = normalizeValue(profileDraft.dob);
    const nextCid = normalizeValue(profileDraft.cid);
    if (profileProgramUsesFacility && !normalizeValue(profileDraft.optionFacilityId)) {
      toast.error('Facility is required.');
      return;
    }

    if (hasFullAccess && !nextDob) {
      toast.error('Date of birth is required.');
      return;
    }

    if (hasFullAccess && !nextCid) {
      toast.error('CID is required.');
      return;
    }

    const variables = buildMedcallProfileMutationVariables({
      draft: {
        cid: nextCid,
        dob: nextDob,
        optionFacilityId: profileDraft.optionFacilityId,
        program: nextProgram,
        provider: nextProvider,
      },
      hasFullAccess,
      participantId: activeParticipantId,
      programUsesFacility: profileProgramUsesFacility,
      routeBase,
    });
    await saveParticipantChange({ action: 'profile', mutate: () => updateParticipantProfile({ variables }), resultField: 'medcallUpdateParticipantProfile', failureMessage: 'Unable to update client.', successMessage: 'Client updated.' });
  };

  const handleSaveStaffAssignment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextStaffId = Number(staffDraft);
    if (isMedcall2Page && nextStaffId === participant.user_id) return;
    if (!Number.isInteger(nextStaffId) || nextStaffId < 1) {
      toast.error('Select a staff member.');
      return;
    }

    await saveParticipantChange({
      action: 'staff',
      mutate: () => updateParticipantStaff({
        variables: {
          newStaffId: nextStaffId,
          participantId: activeParticipantId,
        },
      }),
      resultField: 'medcallUpdateParticipantStaff',
      failureMessage: 'Unable to update staff assignment.',
      successMessage: 'Staff assignment updated.',
    });
  };

  const savePersonAssignment = async (kind: 'nurse' | 'psychiatrist', values: Omit<Medcall2PersonAssignmentSubmission, 'email'> & { email: string | null }) => {
    const variables = { ...values, participantId: activeParticipantId };
    await saveParticipantChange({
      action: kind,
      mutate: () => kind === 'nurse' ? updateParticipantNurse({ variables }) : updateParticipantPsychiatrist({ variables }),
      resultField: kind === 'nurse' ? 'medcallUpdateParticipantNurse' : 'medcallUpdateParticipantPsychiatrist',
      failureMessage: isMedcall2Page ? 'Unable to update assignment.' : kind === 'nurse' ? 'Unable to update nurse.' : 'Unable to update psychiatrist.',
      successMessage: isMedcall2Page ? undefined : kind === 'nurse' ? 'Nurse updated.' : 'Psychiatrist updated.',
      refreshFailureMessage: 'The assignment was saved, but client details could not be refreshed. Reopen the client.',
    });
  };

  const handleSaveNurse = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const firstname = normalizeValue(nurseDraft.firstname);
    const lastname = normalizeValue(nurseDraft.lastname);
    const title = normalizeValue(nurseDraft.title);
    const email = normalizeEmail(nurseDraft.email);
    const confirmEmail = normalizeEmail(nurseDraft.confirmEmail);
    if (!firstname || !lastname || !title || !email) {
      toast.error('First name, last name, title, and email are required.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email address.');
      return;
    }

    if (isBlockedEmailDomain(email, filterDomains)) {
      toast.error('Use an agency email address.');
      return;
    }

    if (confirmEmail !== email) {
      toast.error('The email addresses do not match.');
      return;
    }

    await savePersonAssignment('nurse', { email, firstname, lastname, title });
  };

  const handleSavePsychiatrist = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const firstname = normalizeValue(psychiatristDraft.firstname);
    const lastname = normalizeValue(psychiatristDraft.lastname);
    const title = normalizeValue(psychiatristDraft.title);
    const email = normalizeEmail(psychiatristDraft.email);
    const confirmEmail = normalizeEmail(psychiatristDraft.confirmEmail);
    if (!firstname || !lastname || !title) {
      toast.error('First name, last name, and title are required.');
      return;
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email address.');
      return;
    }

    if (email && isBlockedEmailDomain(email, filterDomains)) {
      toast.error('Use an agency email address.');
      return;
    }

    if (email && confirmEmail !== email) {
      toast.error('The email addresses do not match.');
      return;
    }

    await savePersonAssignment('psychiatrist', { email: email || null, firstname, lastname, title });
  };

  const handleAddNote = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pendingAction !== null) return;

    const noteText = normalizeValue(noteDraft);
    if (!noteText) {
      toast.error('Enter a note before saving.');
      return;
    }

    setPendingAction('note');

    try {
      const result = await addParticipantNote({
        variables: {
          note: noteText,
          participantId: activeParticipantId,
        },
      });
      const savedNoteId = result.data?.medcallAddParticipantNote?.id;
      if (!result.errors?.length && Number.isSafeInteger(savedNoteId) && savedNoteId > 0) {
        toast.success('Note added.');
        setNoteDraft('');
        try {
          await refetch();
        } catch {
          toast.error('The note was saved, but client details could not be refreshed. Reopen the client.');
        }
        return;
      }

      toast.error('Unable to save note.');
    } catch {
      toast.error('Unable to save note.');
    } finally {
      setPendingAction(null);
    }
  };

  const handleAddAlias = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isMedcall2Page && !showAliasEntryFields) {
      closeDialog();
      return;
    }

    const firstname = normalizeValue(aliasDraft.firstname);
    const lastname = normalizeValue(aliasDraft.lastname);
    if (!firstname) {
      toast.error('Firstname is required.');
      return;
    }

    if (!lastname) {
      toast.error('Lastname is required.');
      return;
    }

    await saveParticipantChange({
      action: 'alias',
      mutate: () => addParticipantAlias({
        variables: {
          firstname,
          lastname,
          participantId: activeParticipantId,
        },
      }),
      resultField: 'medcallAddParticipantAlias',
      failureMessage: 'Unable to add alias.',
      successMessage: 'Alias added.',
    });
  };

  const handleDeleteNote = async (noteId = deleteNoteTarget?.id) => {
    if (pendingAction !== null || !noteId) return false;

    setPendingAction(`delete-note-${noteId}`);
    try {
      const result = await deleteParticipantNote({
        variables: {
          noteId,
          participantId: activeParticipantId,
        },
      });
      const response = result.data?.medcallDeleteParticipantNote;
      if (!result.errors?.length && response?.responseCode === 200 && response.responseID === noteId) {
        toast.success('Note deleted.');
        try {
          await refetch();
        } catch {
          toast.error('The note was deleted, but client details could not be refreshed. Reopen the client.');
        }
        setDeleteNoteTarget(null);
        return true;
      }

      toast.error('Unable to delete note.');
    } catch {
      toast.error('Unable to delete note.');
    } finally {
      setPendingAction(null);
    }
    return false;
  };

  const handleSaveStatus = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    const nextActive = !Boolean(participant.active);
    let resolvedReason = '';
    if (!nextActive) {
      if (!statusDraft.reason) {
        toast.error('Select a reason for deactivation.');
        return;
      }

      resolvedReason = statusDraft.reason === 'Other'
        ? `Other - ${normalizeValue(statusDraft.otherReason)}`
        : statusDraft.reason;
      if (statusDraft.reason === 'Other' && !normalizeValue(statusDraft.otherReason)) {
        toast.error('Enter a reason for deactivation.');
        return;
      }

      if (!statusDraft.activationBackDate) {
        toast.error('Select a deactivation date.');
        return;
      }

      if (statusDraft.activationBackDate < minDeactivationDate || statusDraft.activationBackDate > maxDeactivationDate) {
        toast.error('Choose a deactivation date within the last 7 days.');
        return;
      }
    }

    const variables: { active: boolean; participantId: number; activationBackDate?: string; deactivationReason?: string } = {
      active: nextActive,
      participantId: activeParticipantId,
    };

    if (!nextActive) {
      variables.activationBackDate = statusDraft.activationBackDate;
      variables.deactivationReason = resolvedReason;
    }

    await saveParticipantChange({ action: 'status', mutate: () => setParticipantActive({ variables }), resultField: 'medcallSetParticipantActive', failureMessage: 'Unable to update client status.', successMessage: nextActive ? 'Client activated.' : 'Client deactivated.' });
  };

  const handleMedicationRouteChange = (value: string) => {
    const nextFrequencyOptions = getMedicationFrequencyOptions(value);
    const nextFrequency = nextFrequencyOptions.some((option) => option.value === medicationDraft.frequency)
      ? medicationDraft.frequency
      : '';
    const nextScheduleOptions = getMedicationScheduleOptions(nextFrequency);
    setMedicationDraft((current) => ({
      ...current,
      administrationNotes: value === 'IM-LAI' ? current.administrationNotes : '',
      dateDue: value === 'IM-LAI' ? current.dateDue : '',
      dateGiven: value === 'IM-LAI' ? current.dateGiven : '',
      dateInjectionArrived: value === 'IM-LAI' ? current.dateInjectionArrived : '',
      frequency: nextFrequency,
      importanceLevel: value === 'IM-LAI' ? '2' : current.importanceLevel,
      onSite: value === 'IM-LAI' ? current.onSite : '0',
      prn: value === 'IM-LAI' ? false : current.prn,
      psychotropicMeds: value === 'IM-LAI' ? false : current.psychotropicMeds,
      refillDate: value === 'IM-LAI' ? '' : current.refillDate,
      refillStatus: value === 'IM-LAI' ? false : current.refillStatus || !isMedicationReadOnlyProgram,
      route: value,
      startDate: value === 'IM-LAI' ? '' : current.startDate,
      stopDate: value === 'IM-LAI' ? '' : current.stopDate,
      stopDateEnabled: value === 'IM-LAI' ? false : current.stopDateEnabled,
      timesPerDay: nextScheduleOptions.includes(current.timesPerDay)
        ? current.timesPerDay
        : (nextScheduleOptions[0] || (value === 'IM-LAI' ? 'qday' : '')),
    }));
  };

  const handleMedicationFrequencyChange = (value: string) => {
    const nextScheduleOptions = getMedicationScheduleOptions(value);
    setMedicationDraft((current) => ({
      ...current,
      customDays: value === 'custom_days' ? current.customDays : [],
      frequency: value,
      timesPerDay: nextScheduleOptions.includes(current.timesPerDay)
        ? current.timesPerDay
        : (nextScheduleOptions[0] || ''),
    }));
  };

  const handleMedicationDayToggle = (dayValue: string, checked: boolean) => {
    setMedicationDraft((current) => {
      const currentDays = new Set(current.customDays);
      if (checked) {
        currentDays.add(dayValue);
      }

      if (!checked) {
        currentDays.delete(dayValue);
      }

      return {
        ...current,
        customDays: Array.from(currentDays),
      };
    });
  };

  const executeMedicationSave = async (variables: MedicationSaveVariables) => {
    if (pendingAction !== null) return;
    setPendingAction(editingMedicationId ? 'medication-update' : 'medication-create');
    const failureMessage = editingMedicationId ? 'Unable to update medication.' : 'Unable to add medication.';

    try {
      const result = editingMedicationId
        ? await updateMedication({
            variables: {
              ...variables,
              medicationId: editingMedicationId,
            },
          })
        : await createMedication({ variables });

      const response = editingMedicationId
        ? result.data?.medcallUpdateMedication
        : result.data?.medcallCreateMedication;
      const saved = !result.errors?.length && Number.isSafeInteger(response?.id) && response.id > 0 && (!editingMedicationId || response.id === editingMedicationId);
      if (saved) {
        toast.success(editingMedicationId ? 'Medication updated.' : 'Medication added.');
        try {
          await refetch();
        } catch {
          toast.error('The medication was saved, but client details could not be refreshed. Reopen the client.');
        }
        closeDialog();
        return;
      }

      toast.error(failureMessage);
    } catch {
      toast.error(failureMessage);
    } finally {
      setPendingAction(null);
    }
  };

  const handleConfirmMedicationSave = async () => {
    if (!medicationPreviewVariables || medicationPreviewSubmittingRef.current) {
      return;
    }

    medicationPreviewSubmittingRef.current = true;
    try {
      await executeMedicationSave(medicationPreviewVariables);
    } finally {
      medicationPreviewSubmittingRef.current = false;
    }
  };

  const handleSaveMedication = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const medicineName = normalizeValue(medicationDraft.medicineName);
    const strength = normalizeValue(medicationDraft.strength);
    const route = normalizeValue(medicationDraft.route);
    const frequency = normalizeValue(medicationDraft.frequency).toLowerCase();
    const timesPerDay = normalizeValue(medicationDraft.timesPerDay).toLowerCase();
    if (!medicineName || !strength || !route || !frequency || !timesPerDay) {
      toast.error('Medication name, dose, route, frequency, and schedule are required.');
      return;
    }

    if (frequency === 'custom_days' && medicationDraft.customDays.length < 1) {
      toast.error('Select at least one custom day.');
      return;
    }

    if (route !== 'IM-LAI' && !medicationDraft.startDate) {
      toast.error('Start date and time are required.');
      return;
    }

    if (route !== 'IM-LAI' && medicationDraft.stopDateEnabled && !medicationDraft.stopDate) {
      toast.error('Enter the discontinue date and time.');
      return;
    }

    if (route !== 'IM-LAI' && !isMedicationReadOnlyProgram && medicationDraft.refillStatus && !medicationDraft.refillDate) {
      toast.error('Enter the runs out date.');
      return;
    }

    if (route === 'IM-LAI' && !medicationDraft.dateDue) {
      toast.error('Enter the injection due date.');
      return;
    }

    if (route === 'IM-LAI' && medicationDraft.onSite === '2' && !medicationDraft.dateInjectionArrived) {
      toast.error('Enter the date the injection arrived on-site.');
      return;
    }

    if (route === 'IM-LAI' && medicationDraft.dateGiven && !normalizeValue(medicationDraft.administrationNotes)) {
      toast.error('Enter the injection site location.');
      return;
    }

    const variables: MedicationSaveVariables = {
        administrationNotes: normalizeValue(medicationDraft.administrationNotes) || null,
        daysOfWeek: frequency === 'custom_days' ? medicationDraft.customDays : [],
        frequency,
        importanceLevel: route === 'IM-LAI' ? 2 : Number(medicationDraft.importanceLevel || 2),
        injectionDateArrived: route === 'IM-LAI' && medicationDraft.onSite === '2'
          ? (medicationDraft.dateInjectionArrived || null)
          : null,
        injectionDateDue: route === 'IM-LAI' ? (medicationDraft.dateDue || null) : null,
        injectionDateGiven: route === 'IM-LAI' ? (medicationDraft.dateGiven || null) : null,
        injectionOnSite: route === 'IM-LAI' ? Number(medicationDraft.onSite || 0) : 0,
        medicineDetail: normalizeValue(medicationDraft.medicineDetail) || null,
        medicineName,
        participantId: activeParticipantId,
        prn: route === 'IM-LAI' ? false : medicationDraft.prn,
        psychotropicMeds: route === 'IM-LAI' ? false : medicationDraft.psychotropicMeds,
        refillDate: route === 'IM-LAI' || isMedicationReadOnlyProgram || !medicationDraft.refillStatus
          ? null
          : (medicationDraft.refillDate || null),
        refillStatus: route === 'IM-LAI' || isMedicationReadOnlyProgram ? false : medicationDraft.refillStatus,
        route,
        startDate: route === 'IM-LAI' ? null : (medicationDraft.startDate || null),
        stopDate: route === 'IM-LAI' || !medicationDraft.stopDateEnabled ? null : (medicationDraft.stopDate || null),
        stopDateStatus: route === 'IM-LAI' ? false : medicationDraft.stopDateEnabled,
        strength,
        timesPerDay,
      };

    if (isMedcall2Page) {
      setMedicationPreviewVariables(variables);
      return;
    }

    await executeMedicationSave(variables);
  };

  const handleToggleMedicationStatus = async (medication: MedcallParticipantMedication) => {
    if (pendingAction !== null) return;

    const nextActive = !Boolean(medication.active);
    setPendingAction(`medication-status-${medication.id}`);

    try {
      const result = await setMedicationActive({
        variables: {
          active: nextActive,
          medicationId: medication.id,
        },
      });
      const confirmed = !result.errors?.length && result.data?.medcallSetMedicationActive?.id === medication.id;
      if (confirmed) {
        toast.success(nextActive ? 'Medication activated.' : 'Medication discontinued.');
        try {
          await refetch();
        } catch {
          toast.error('The medication status was saved, but client details could not be refreshed. Reopen the client.');
        }
        return;
      }

      toast.error('Unable to update medication.');
    } catch {
      toast.error('Unable to update medication.');
    } finally {
      setPendingAction(null);
    }
  };

  const handleDeleteMedication = async (medication: MedcallParticipantMedication) => {
    if (pendingAction !== null) return;

    if (Boolean(medication.active)) {
      toast.error('Discontinue the medication before deleting it.');
      return;
    }

    if (!window.confirm(`Delete ${normalizeValue(medication.medicine_name) || 'this medication'}?`)) {
      return;
    }

    setPendingAction(`medication-delete-${medication.id}`);

    try {
      const result = await deleteMedication({
        variables: {
          medicationId: medication.id,
        },
      });
      const response = result.data?.medcallDeleteMedication;
      const confirmed = !result.errors?.length && response?.responseCode === 200 && response.responseID === medication.id;
      if (confirmed) {
        toast.success('Medication deleted.');
        try {
          await refetch();
        } catch {
          toast.error('The medication was deleted, but client details could not be refreshed. Reopen the client.');
        }
        return;
      }

      toast.error('Unable to delete medication.');
    } catch {
      toast.error('Unable to delete medication.');
    } finally {
      setPendingAction(null);
    }
  };

  const handleSaveMedicationTimelineStatus = async (
    timeline: MedcallMedicationTimeline,
    nextStatus: 'notTaken' | 'taken' | 'unset'
  ) => {
    if (pendingAction !== null) return;

    setPendingAction(`timeline-status-${timeline.id}`);

    try {
      const result = await updateMedicationTimeline({
        variables: {
          preserveLegacySemantics: isMedcall2Page,
          status: nextStatus,
          time: isMedcall2Page ? getLegacyMedcallTimelineSubmissionTime() : undefined,
          timelineId: timeline.id,
        },
      });
      if (didConfirmMedicationTimelineMutation(result, 'medcallUpdateMedicationTimeline', timeline.id, true)) {
        if (isMedcall2Page) {
          await waitForLegacyTimelinePatch();
          setMedicationTimelineOverrides((current) => new Map(current).set(timeline.id, {
            ...(current.get(timeline.id) || {}),
            status: nextStatus,
          }));
          setPendingAction(null);
          return;
        }

        toast.success('Medication timeline updated.');
        try {
          await refetch();
        } catch {
          toast.error('The medication timeline was saved, but client details could not be refreshed. Reopen the client.');
        }
        setPendingAction(null);
        return;
      }

      toast.error('Unable to update medication timeline.');
    } catch {
      toast.error('Unable to update medication timeline.');
    }

    setPendingAction(null);
  };

  const handleSaveMedicationTimelineWitness = async (
    timeline: MedcallMedicationTimeline,
    witnessedByOther: boolean
  ) => {
    if (pendingAction !== null) return;

    setPendingAction(`timeline-witness-${timeline.id}`);

    try {
      const result = await updateMedicationTimeline({
        variables: {
          preserveLegacySemantics: isMedcall2Page,
          timelineId: timeline.id,
          witnessedByOther,
        },
      });
      if (didConfirmMedicationTimelineMutation(result, 'medcallUpdateMedicationTimeline', timeline.id, true)) {
        if (isMedcall2Page) {
          await waitForLegacyTimelinePatch();
          setMedicationTimelineOverrides((current) => new Map(current).set(timeline.id, {
            ...(current.get(timeline.id) || {}),
            witnessed_by_other: witnessedByOther,
          }));
          setPendingAction(null);
          return;
        }

        toast.success('Witness status updated.');
        try {
          await refetch();
        } catch {
          toast.error('The medication timeline was saved, but client details could not be refreshed. Reopen the client.');
        }
        setPendingAction(null);
        return;
      }

      toast.error('Unable to update witness status.');
    } catch {
      toast.error('Unable to update witness status.');
    }

    setPendingAction(null);
  };

  const handleSaveMedicationTimelineSite = async (
    timeline: MedcallMedicationTimeline,
    site: string
  ) => {
    if (pendingAction !== null) return;

    setPendingAction(`timeline-site-${timeline.id}`);

    try {
      const result = await updateMedicationTimelineSite({
        variables: {
          preserveLegacySemantics: isMedcall2Page,
          site: isMedcall2Page ? site : site || null,
          timelineId: timeline.id,
        },
      });
      if (didConfirmMedicationTimelineMutation(result, 'medcallUpdateMedicationTimelineSite', timeline.id, true)) {
        if (isMedcall2Page) {
          await waitForLegacyTimelinePatch();
          setMedicationTimelineOverrides((current) => new Map(current).set(timeline.id, {
            ...(current.get(timeline.id) || {}),
            site,
          }));
          setPendingAction(null);
          return;
        }

        toast.success('Medication site updated.');
        try {
          await refetch();
        } catch {
          toast.error('The medication timeline was saved, but client details could not be refreshed. Reopen the client.');
        }
        setPendingAction(null);
        return;
      }

      toast.error('Unable to update medication site.');
    } catch {
      toast.error('Unable to update medication site.');
    }

    setPendingAction(null);
  };

  const handleSaveMedicationAdministrationNotes = async (
    timeline: MedcallMedicationTimeline,
    administrationNotes: string
  ) => {
    if (!isMedcall2Page) {
      setPendingAction(`timeline-administration-notes-${timeline.id}`);
    }

    try {
      const result = await updateMedicationAdministrationNotes({
        variables: {
          administrationNotes,
          preserveLegacySemantics: isMedcall2Page,
          timelineId: timeline.id,
        },
      });
      if (didConfirmMedicationTimelineMutation(result, 'medcallUpdateMedicationAdministrationNotes', timeline.id)) {
        if (isMedcall2Page) {
          const injectionInfo = Array.isArray(timeline.injection_info) && timeline.injection_info[0]
            ? [{ ...timeline.injection_info[0], administration_notes: administrationNotes }]
            : [];
          setMedicationTimelineOverrides((current) => new Map(current).set(timeline.id, {
            ...(current.get(timeline.id) || {}),
            injection_info: injectionInfo,
          }));
          return true;
        }

        await refetch();
        setPendingAction(null);
        return true;
      }
    } catch (_mutationError) {
      if (!isMedcall2Page) {
        setPendingAction(null);
      }
      return false;
    }

    if (!isMedcall2Page) {
      setPendingAction(null);
    }
    return false;
  };

  const openProjectSitesTrackerSelection = (selection: ProjectSitesTrackerSelection) => {
    const currentStatus = projectSitesStatusLookup.get(selection.startDate);
    setProjectSitesTrackerSelection(selection);
    setProjectSitesTrackerStatus(currentStatus?.status === 'taken' ? 'taken' : 'notTaken');
    setProjectSitesTrackerNotes(normalizeValue(currentStatus?.notes));
  };

  const handleSaveProjectSitesTrackerSelection = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!projectSitesTrackerSelection || pendingAction !== null) {
      return;
    }

    setPendingAction('project-sites-tracker');

    try {
      const result = await updateProjectSitesMedicationStatuses({
        variables: {
          endDate: projectSitesTrackerSelection.endDate,
          notes: normalizeValue(projectSitesTrackerNotes) || null,
          participantId: activeParticipantId,
          startDate: projectSitesTrackerSelection.startDate,
          status: projectSitesTrackerStatus,
        },
      });
      const rows: MedcallProjectSitesMedicationStatus[] | null | undefined = result.data?.medcallUpdateProjectSitesMedicationStatuses;
      const startDate = parseUtcDateOnly(projectSitesTrackerSelection.startDate);
      const endDate = parseUtcDateOnly(projectSitesTrackerSelection.endDate || projectSitesTrackerSelection.startDate);
      const expectedDates: string[] = [];
      if (startDate && endDate) {
        let date = startDate;
        while (date <= endDate) {
          expectedDates.push(formatUtcDateOnly(date));
          date = addUtcDays(date, 1);
        }
      }
      const requestedRows = Array.isArray(rows)
        ? rows.filter((row) => expectedDates.includes(normalizeValue(row.date)))
        : [];
      const confirmed = !result.errors?.length && expectedDates.length > 0
        && requestedRows.length === expectedDates.length
        && new Set(requestedRows.map((row) => row.date)).size === expectedDates.length
        && requestedRows.every((row) => Number.isSafeInteger(row.id) && Number(row.id) > 0 && row.status === projectSitesTrackerStatus);
      if (confirmed) {
        toast.success('Tracker updated.');
        try {
          await refetch();
        } catch {
          toast.error('The tracker was saved, but client details could not be refreshed. Reopen the client.');
        }
        setProjectSitesTrackerSelection(null);
        return;
      }

      toast.error('Unable to update tracker.');
    } catch {
      toast.error('Unable to update tracker.');
    } finally {
      setPendingAction(null);
    }
  };

  const handleSaveProjectSitesWeekStart = async (nextWeekStart: string) => {
    if (pendingAction !== null) return;
    const previousWeekStart = projectSitesWeekStartDraft;
    setProjectSitesWeekStartDraft(nextWeekStart);
    setPendingAction('project-sites-options');

    try {
      const result = await updateProjectSitesClientOptions({
        variables: {
          participantId: activeParticipantId,
          weekStart: Number(nextWeekStart),
        },
      });
      const response = result.data?.medcallUpdateProjectSitesClientOptions;
      if (!result.errors?.length && Number.isSafeInteger(response?.id) && response.id > 0 && response.week_start === Number(nextWeekStart)) {
        toast.success('Tracker settings updated.');
        try {
          await refetch();
        } catch {
          toast.error('The tracker settings were saved, but client details could not be refreshed. Reopen the client.');
        }
        return;
      }

      setProjectSitesWeekStartDraft(previousWeekStart);
      toast.error('Unable to update tracker settings.');
    } catch {
      setProjectSitesWeekStartDraft(previousWeekStart);
      toast.error('Unable to update tracker settings.');
    } finally {
      setPendingAction(null);
    }
  };

  const participantActions = (
    <MedcallParticipantActions
      canManageMedications={canManageMedications}
      canManageParticipant={canManageParticipant}
      isNurselessProgram={isNurselessProgram}
      isOnMyClients={isOnMyClients}
      onAddMedication={openCreateMedicationDialog}
      onAddToMyClients={handleAddToMyClients}
      onAssignNurse={openNurseDialog}
      onAssignPsychiatrist={openPsychiatristDialog}
      onAssignStaff={openStaffDialog}
      onClientAliases={openAliasesDialog}
      onClientNotes={openNotesDialog}
      onEditClient={openEditDialog}
      onMedcallMessage={openMessageDialog}
      onMedicationList={() => setActiveDialog('med-list')}
      onParticipantStatus={openStatusDialog}
      onRemoveFromMyClients={handleRemoveFromMyClients}
      onReport={() => setActiveDialog('report')}
      participantActive={Boolean(participant.active)}
      pending={pendingAction !== null}
      renderParticipantStatusAction={isMedcall2Page && participant.active ? (button) => (
        <Medcall2ParticipantStatusPopover
          draft={statusDraft}
          maxDate={maxDeactivationDate}
          minDate={minDeactivationDate}
          onCancel={closeDialog}
          onChange={setStatusDraft}
          onSubmit={handleSaveStatus}
          open={activeDialog === 'status'}
          pending={pendingAction !== null}
          reasons={DEACTIVATION_OPTIONS}
        >{button}</Medcall2ParticipantStatusPopover>
      ) : undefined}
    />
  );
  const psychiatristLabel = isMedcall2Page
    ? formatLegacyPersonLabel(participant.psychiatrist)
    : formatPersonLabel(participant.psychiatrist);
  const nurseLabel = isMedcall2Page
    ? formatLegacyPersonLabel(participant.nurse_name)
    : formatPersonLabel(participant.nurse_name);

  return (
    <div className="space-y-4">
      {!embedded && !isMedcall2Page ? (
        <Button
          className="medcall-participant-back"
          onClick={() => navigate(backPath)}
          size="sm"
          variant="ghost"
        >
          Back to List
        </Button>
      ) : null}

      {isMedcall2Page && !embedded ? (
        <Medcall2ParticipantIdentityCard
          actions={participantActions}
          onBack={() => navigate(-1)}
          participant={{
            active: Boolean(participant.active),
            activationHistory,
            addedAt: participant.created_at,
            aliases: participant.aliases,
            cid: participant.cid,
            dob: participant.dob,
            facility: participant.facility,
            firstname: participant.firstname,
            isNurselessProgram,
            lastname: participant.lastname,
            nurse: nurseLabel === '—' ? '' : nurseLabel,
            participantId: participant.participant_id,
            program: participant.program,
            provider: participant.provider,
            psychiatrist: psychiatristLabel === '—' ? '' : psychiatristLabel,
            staffAssigned: participant.staff_assigned,
          }}
        />
      ) : (
        <Card>
          <CardHeader className={embedded ? 'gap-4' : 'gap-2 px-3 py-3'}>
            <div className={embedded ? 'flex flex-wrap items-start justify-between gap-4' : 'flex min-w-0 flex-col gap-2'}>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-2xl">
                    {normalizeValue(participant.firstname)} {normalizeValue(participant.lastname)}
                  </CardTitle>
                  <Badge
                    style={getActiveBadgeStyle(Boolean(participant.active))}
                    variant="outline"
                  >
                    {participant.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
              {participantActions}
            </div>
          </CardHeader>
          <CardContent className={`grid gap-x-3 gap-y-2 pt-2 ${embedded ? 'grid-cols-7' : 'sm:grid-cols-2 xl:grid-cols-4'}`}>
            <DetailField compact={embedded} label="Aliases" value={formatAliases(participant.aliases)} />
            <DetailField compact={embedded} label="Program" value={participantProgram || '—'} />
            <DetailField compact={embedded} label="Date Added" value={formatDateTime(participant.created_at)} />
            {!embedded ? <DetailField label="DOB" value={formatDate(participant.dob)} /> : null}
            {!embedded ? <DetailField label="CID" value={normalizeValue(participant.cid) || '—'} /> : null}
            {!embedded ? <DetailField label="Provider" value={normalizeValue(participant.provider) || '—'} /> : null}
            {!isNurselessProgram ? (
              <DetailField compact={embedded} label="Facility" value={normalizeValue(participant.facility) || '—'} />
            ) : null}
            <DetailField compact={embedded} label="Psychiatrist" value={formatPersonLabel(participant.psychiatrist)} />
            <DetailField compact={embedded} label="Assigned Staff" value={normalizeValue(participant.staff_assigned) || '—'} />
            {!isNurselessProgram ? (
              <DetailField compact={embedded} label="Nurse" value={formatPersonLabel(participant.nurse_name)} />
            ) : null}
          </CardContent>
        </Card>
      )}

      {isProjectSitesClient ? (
        <Card>
          <CardHeader className="gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>Medication Tracker</CardTitle>
                <CardDescription>Track daily medication status for this client.</CardDescription>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Tabs onValueChange={(value) => setProjectSitesTrackerView(value as ProjectSitesTrackerView)} value={projectSitesTrackerView}>
                  <TabsList className="h-auto rounded-[var(--border-radius-medium)] bg-[var(--surface-muted)] p-1">
                    {PROJECT_SITES_TRACKER_VIEW_OPTIONS.map((option) => (
                      <TabsTrigger
                        className="rounded-[var(--border-radius-small)] px-3 py-1.5"
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                <Input
                  className="w-[150px]"
                  onChange={(event) => setProjectSitesTrackerMonth(event.target.value || getLosAngelesDateOnly().slice(0, 7))}
                  type="month"
                  value={projectSitesTrackerMonth}
                />
                <Select
                  disabled={!canManageParticipant || pendingAction !== null}
                  onValueChange={handleSaveProjectSitesWeekStart}
                  value={projectSitesWeekStartDraft}
                >
                  <SelectTrigger className="w-[190px]">
                    <SelectValue placeholder="Week Start" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_SITES_WEEK_START_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={`${option.value}`}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            {projectSitesTrackerView === 'day' ? (
              <div className="space-y-2">
                <div className="grid grid-cols-7 gap-2">
                  {projectSitesWeekdayLabels.map((label) => (
                    <div
                      className="px-2 text-center text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-90)]"
                      key={label}
                    >
                      {label}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {projectSitesDayGridDates.map((date) => {
                    const dateKey = formatUtcDateOnly(date);
                    const selectedMonth = parseUtcDateOnly(`${projectSitesTrackerMonth}-01`);
                    const isCurrentMonth = Boolean(
                      selectedMonth
                      && date.getUTCFullYear() === selectedMonth.getUTCFullYear()
                      && date.getUTCMonth() === selectedMonth.getUTCMonth()
                    );
                    const statusLabel = getProjectSitesStatusLabel(projectSitesStatusLookup, dateKey, dateKey);
                    const isTaken = statusLabel === 'Taken';
                    return (
                      <button
                        className={[
                          'min-h-[72px] rounded-[var(--border-radius-medium)] border px-2 py-2 text-left transition',
                          isCurrentMonth
                            ? isTaken
                              ? 'border-[var(--semantic-success)] bg-[var(--semantic-success-soft)] text-[var(--semantic-success)]'
                              : 'border-[var(--neutral-gray-30)] bg-white text-[var(--neutral-gray-120)] hover:bg-[var(--surface-muted)]'
                            : 'border-[var(--neutral-gray-20)] bg-[var(--surface-muted)] text-[var(--neutral-gray-70)]',
                        ].join(' ')}
                        disabled={!canManageParticipant || !isCurrentMonth || pendingAction !== null}
                        key={dateKey}
                        onClick={() => openProjectSitesTrackerSelection({
                          endDate: null,
                          label: formatDate(dateKey),
                          startDate: dateKey,
                        })}
                        type="button"
                      >
                        <div className="text-sm font-semibold">{date.getUTCDate()}</div>
                        <div className="mt-2 text-xs uppercase tracking-wide">{statusLabel}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {projectSitesTrackerView === 'week' ? (
              <div className="grid gap-2 md:grid-cols-2">
                {projectSitesWeekRanges.map((range) => {
                  const statusLabel = getProjectSitesStatusLabel(projectSitesStatusLookup, range.startDate, range.endDate);
                  const isTaken = statusLabel === 'Taken';
                  return (
                    <button
                      className={[
                        'rounded-[var(--border-radius-medium)] border px-3 py-3 text-left transition',
                        isTaken
                          ? 'border-[var(--semantic-success)] bg-[var(--semantic-success-soft)] text-[var(--semantic-success)]'
                          : 'border-[var(--neutral-gray-30)] bg-white text-[var(--neutral-gray-120)] hover:bg-[var(--surface-muted)]',
                      ].join(' ')}
                      disabled={!canManageParticipant || pendingAction !== null}
                      key={range.startDate}
                      onClick={() => openProjectSitesTrackerSelection(range)}
                      type="button"
                    >
                      <div className="text-sm font-semibold">{range.label}</div>
                      <div className="mt-1 text-xs uppercase tracking-wide">{statusLabel}</div>
                    </button>
                  );
                })}
              </div>
            ) : null}

            {projectSitesTrackerView === 'month' ? (
              <div className="grid gap-2 md:grid-cols-3">
                {projectSitesMonthRanges.map((range) => {
                  const statusLabel = getProjectSitesStatusLabel(projectSitesStatusLookup, range.startDate, range.endDate);
                  const isTaken = statusLabel === 'Taken';
                  return (
                    <button
                      className={[
                        'rounded-[var(--border-radius-medium)] border px-3 py-3 text-left transition',
                        isTaken
                          ? 'border-[var(--semantic-success)] bg-[var(--semantic-success-soft)] text-[var(--semantic-success)]'
                          : 'border-[var(--neutral-gray-30)] bg-white text-[var(--neutral-gray-120)] hover:bg-[var(--surface-muted)]',
                      ].join(' ')}
                      disabled={!canManageParticipant || pendingAction !== null}
                      key={range.startDate}
                      onClick={() => openProjectSitesTrackerSelection(range)}
                      type="button"
                    >
                      <div className="text-sm font-semibold">{range.label}</div>
                      <div className="mt-1 text-xs uppercase tracking-wide">{statusLabel}</div>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <div className={isMedcall2Page ? 'medcall2-legacy-medication-wrap' : 'grid gap-4 xl:grid-cols-[1.25fr_0.75fr]'}>
        <Card className={isMedcall2Page ? 'medcall2-legacy-medication-accordion' : ''}>
          {!isMedcall2Page ? <CardHeader className="gap-0 px-4 py-2.5">
            <div className="flex flex-nowrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-nowrap items-center gap-1.5">
                <CardTitle>Medications</CardTitle>
                <Tabs className="shrink-0" onValueChange={(value) => setMedicationView(value as MedcallMedicationView)} value={medicationView}>
                  <TabsList className="h-6 rounded-[var(--border-radius-small)] bg-[var(--surface-muted)] p-0.5">
                    <TabsTrigger className="h-5 rounded-[var(--border-radius-small)] px-2 py-0 text-[10px]" value="active">
                      Active
                    </TabsTrigger>
                    <TabsTrigger className="h-5 rounded-[var(--border-radius-small)] px-2 py-0 text-[10px]" value="historical">
                      Historical
                    </TabsTrigger>
                    <TabsTrigger className="h-5 rounded-[var(--border-radius-small)] px-2 py-0 text-[10px]" value="prn">
                      PRN
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              {canManageMedications ? (
                <div className={ACTION_LINK_GROUP_CLASS}>
                  <Button
                    disabled={pendingAction !== null}
                    id="medcall-medications-add-button"
                    onClick={openCreateMedicationDialog}
                    size="sm"
                  >
                    <Plus className="h-4 w-4" />
                    Add Medication
                  </Button>
                </div>
              ) : null}
            </div>
          </CardHeader> : null}
          <CardContent className={isMedcall2Page ? 'medcall2-legacy-medication-grid' : 'space-y-4 pt-2'}>
            {(isMedcall2Page ? [
              { key: 'regular', medications: visibleLegacyNonPrnMedications },
              { key: 'prn', medications: legacyPrnMedications },
            ] : [{ key: 'existing', medications: filteredMedications }]).map((group) => (
            <MedicationColumn key={group.key} legacy={isMedcall2Page}>
            {isMedcall2Page ? (
              <>
                {group.key === 'regular' ? (
                <div className="medcall2-legacy-medication-filter medcall2-legacy-medication-filter--regular">
                  {(['all', 'active', 'historical'] as const).map((value) => (
                    <button
                      className={medicationView === value ? 'is-selected' : ''}
                      key={value}
                      onClick={() => setMedicationView(value)}
                      type="button"
                    >
                      {value === 'all' ? 'All' : value === 'active' ? 'Active' : 'Historical'}
                    </button>
                  ))}
                </div>
                ) : <div className="medcall2-legacy-medication-filter medcall2-legacy-medication-filter--prn">
                  <span>PRN</span>
                </div>}
                {group.medications.length < 1 ? (
                  <div className="medcall2-legacy-medication-empty">No Medication</div>
                ) : null}
              </>
            ) : null}
            {!isMedcall2Page && filteredMedications.length < 1 ? (
              <div className="text-sm text-[var(--neutral-gray-100)]">No medications recorded for this view.</div>
            ) : null}

            {group.medications.map((medication) => {
              const sourceTimelines = embedded
                ? medicationTimelineHistory.get(medication.id) || []
                : Array.isArray(medication.timelines) ? medication.timelines : [];
              const timelines = sourceTimelines.map((timeline) => {
                const timelineOverride = medicationTimelineOverrides.get(timeline.id);
                if (!timelineOverride) {
                  return timeline;
                }

                return { ...timeline, ...timelineOverride };
              });
              const isImOtherMedication = normalizeValue(medication.route) === 'IM-Other';
              const isImLaiMedication = normalizeValue(medication.route) === 'IM-LAI';
              const hasMedicationHistory = shouldShowMedcallMedicationHistory({
                embedded,
                program: participantProgram,
                route: normalizeValue(medication.route),
                routeBase,
                timelineCount: timelines.length,
              });
              const isMedicationRunOut = isMedcall2Page && isLegacyMedcallMedicationRunOut(
                medication.refills,
                getLosAngelesDateOnly(),
              );
              const medicationWarning = isMedcall2Page
                ? getLegacyMedcallMedicationWarning({
                    injection_info: medication.injection_info,
                    program: participantProgram,
                    refill_status: medication.refill_status,
                    refills: medication.refills,
                    today: getLosAngelesDateOnly(),
                  })
                : null;
              const visibleTimelines = getVisibleMedcallTimelineRows(
                timelines,
                normalizeValue(medication.route),
                routeBase,
              );
              const timelinePage = isMedcall2Page
                ? getMedcallTimelinePage(
                    visibleTimelines,
                    medicationTimelinePages.get(medication.id) || 1,
                  )
                : {
                    page: 1,
                    pageCount: 1,
                    rows: visibleTimelines,
                  };
              const isMedicationHistoryExpanded = expandedMedicationIds.has(medication.id);
              const medicationHistoryToggleLabel = isMedicationHistoryExpanded
                ? isImLaiMedication
                  ? 'Hide Previous Dates'
                  : 'Hide Timeline'
                : isImLaiMedication
                  ? 'Show Previous Dates'
                  : 'Show Timeline';
              return (
                <div
                  className={isMedcall2Page
                    ? `medcall2-legacy-medication${Boolean(medication.prn) && Boolean(medication.active) ? ' medcall2-legacy-medication--prn' : ' medcall2-legacy-medication--regular'}`
                    : 'rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)]'}
                  key={medication.id}
                >
                  {isMedcall2Page ? (
                    <div
                      className={`medcall2-legacy-medication__summary${medicationWarning ? ' has-warning' : ''}${hasMedicationHistory ? '' : ' is-nonexpandable'}`}
                      onClick={() => {
                        if (hasMedicationHistory) void toggleMedicationHistory(medication.id);
                      }}
                      onKeyDown={(event) => {
                        if (hasMedicationHistory && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault();
                          void toggleMedicationHistory(medication.id);
                        }
                      }}
                      role={hasMedicationHistory ? 'button' : undefined}
                      tabIndex={hasMedicationHistory ? 0 : undefined}
                    >
                      <svg
                        aria-hidden="true"
                        className={`medcall2-legacy-medication__chevron${isMedicationHistoryExpanded ? ' is-expanded' : ''}${hasMedicationHistory ? '' : ' is-hidden'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 512 512"
                      >
                        <path d="M112 184l144 144 144-144" strokeLinecap="square" strokeMiterlimit="10" strokeWidth="48" />
                      </svg>
                      <Medcall2MedicationTypeIcon injection={isImLaiMedication || isImOtherMedication} />
                      <div className="medcall2-legacy-medication__details">
                        <div className="medcall2-legacy-medication__name">
                          <strong>Medication:</strong>
                          <span>{getLegacyMedicationDisplayName(medication)}</span>
                        </div>
                        <div className="medcall2-legacy-medication__name">
                          <strong>Dose:</strong>
                          <span>{getLegacyMedicationDose(medication)}</span>
                        </div>
                        <div>
                          <strong>Route:</strong>
                          <span>{normalizeValue(medication.route) || '—'}</span>
                        </div>
                        <div>
                          <strong>Frequency:</strong>
                          <span>{getLegacyMedicationFrequency(medication)}</span>
                        </div>
                        {medicationWarning ? (
                          <div className="medcall2-legacy-medication__warning">{medicationWarning}</div>
                        ) : null}
                      </div>
                      {canManageMedications ? (
                        <div className="medcall2-legacy-medication__tools">
                          <button
                            aria-expanded={openMedicationMenuId === medication.id}
                            aria-label={`Medication actions for ${getLegacyMedicationDisplayName(medication)}`}
                            disabled={pendingAction !== null}
                            onClick={(event) => {
                              event.stopPropagation();
                              setOpenMedicationMenuId((current) => current === medication.id ? null : medication.id);
                            }}
                            type="button"
                          >
                            <Medcall2MedicationSettingsIcon />
                          </button>
                          {openMedicationMenuId === medication.id ? (
                            <div className="medcall2-legacy-medication__menu" role="menu">
                              <button
                                disabled={!Boolean(medication.active)}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOpenMedicationMenuId(null);
                                  openEditMedicationDialog(medication);
                                }}
                                role="menuitem"
                                type="button"
                              >
                                Update Medication
                              </button>
                              <button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOpenMedicationMenuId(null);
                                  void handleToggleMedicationStatus(medication);
                                }}
                                role="menuitem"
                                type="button"
                              >
                                {medication.active ? 'Discontinue' : 'Activate'} Medication
                              </button>
                              <button
                                disabled={Boolean(medication.active)}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setOpenMedicationMenuId(null);
                                  void handleDeleteMedication(medication);
                                }}
                                role="menuitem"
                                type="button"
                              >
                                Delete Medication
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className={`border-b border-[var(--neutral-gray-20)] px-4 py-3${medicationWarning ? ' bg-[#ffebeb]' : ''}`}>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">
                              {normalizeValue(medication.medicine_name) || 'Unnamed medication'}
                            </div>
                            <Badge style={getActiveBadgeStyle(Boolean(medication.active))} variant="outline">
                              {medication.active ? 'Active' : 'Inactive'}
                            </Badge>
                            {medication.prn ? (
                              <Badge style={warningBadgeStyle} variant="outline">PRN</Badge>
                            ) : null}
                          </div>
                          <div className="text-sm text-[var(--neutral-gray-100)]">
                            {[normalizeValue(medication.strength), normalizeValue(medication.route), formatMedicationFrequency(medication)]
                              .filter(Boolean)
                              .join(' • ') || '—'}
                          </div>
                        </div>
                        {canManageMedications ? (
                          <div className={ACTION_LINK_GROUP_CLASS}>
                            <Button disabled={pendingAction !== null} onClick={() => openEditMedicationDialog(medication)} size="sm" variant="link">Edit</Button>
                            <Button disabled={pendingAction !== null} onClick={() => handleToggleMedicationStatus(medication)} size="sm" variant="link">
                              {medication.active ? 'Discontinue' : 'Activate'}
                            </Button>
                            <Button disabled={pendingAction !== null || Boolean(medication.active)} onClick={() => handleDeleteMedication(medication)} size="sm" variant="link">Delete</Button>
                          </div>
                        ) : null}
                      </div>
                      <div className="mt-2 flex flex-nowrap items-center justify-between gap-2">
                        <div className="min-w-0 truncate text-xs uppercase tracking-wide text-[var(--neutral-gray-90)]">
                          Importance {formatImportanceLabel(medication.importance_level)} • Last Given {formatDate(medication.last_taken_date)}
                        </div>
                        {hasMedicationHistory ? (
                          <Button
                            aria-controls={`medication-history-${medication.id}`}
                            aria-expanded={isMedicationHistoryExpanded}
                            className="h-auto shrink-0 px-0 py-0"
                            id={`medcall-medication-${medication.id}-timeline-button`}
                            onClick={() => toggleMedicationHistory(medication.id)}
                            size="sm"
                            type="button"
                            variant="link"
                          >
                            {isMedicationHistoryExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            {medicationHistoryToggleLabel}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {hasMedicationHistory && isMedicationHistoryExpanded ? (
                    <div
                      className={isMedcall2Page ? 'medcall2-legacy-timeline' : 'overflow-x-auto px-4 py-3'}
                      id={`medication-history-${medication.id}`}
                    >
                      {isImLaiMedication ? (
                        <table className="w-full min-w-[720px] text-sm">
                          <thead>
                            <tr className="border-b border-[var(--neutral-gray-30)] text-left text-xs uppercase tracking-wide text-[var(--neutral-gray-90)]">
                              <th className="py-2 pr-3 font-semibold">Frequency</th>
                              <th className="py-2 pr-3 font-semibold">Next Due Date</th>
                              <th className="py-2 pr-3 font-semibold">Status</th>
                              <th className="py-2 pr-3 font-semibold">Date Given</th>
                              <th className="py-2 pr-0 font-semibold">
                                {isMedcall2Page ? 'Injection site location' : 'Injection Site'}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {timelinePage.rows.map((timeline) => {
                              const injectionInfo = Array.isArray(timeline.injection_info) ? timeline.injection_info[0] : null;
                              return (
                                <tr className="border-b border-[var(--neutral-gray-20)] align-top last:border-b-0" key={timeline.id}>
                                  <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">
                                    {isMedcall2Page
                                      ? getLegacyMedcallTimelineFrequencyLabel(medication)
                                      : formatMedicationFrequency(medication)}
                                  </td>
                                  <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">{isMedcall2Page ? formatDate(injectionInfo?.date_due).replace(/\/(\d{2})(\d{2})$/, '/$2') : formatDate(injectionInfo?.date_due)}</td>
                                  <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">
                                    {formatInjectionOnSiteLabel(injectionInfo?.on_site, isMedcall2Page)}
                                  </td>
                                  <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">{isMedcall2Page ? formatDate(injectionInfo?.date_given).replace(/\/(\d{2})(\d{2})$/, '/$2') : formatDate(injectionInfo?.date_given)}</td>
                                  <td className="py-2 pr-0 text-[var(--neutral-gray-120)]">
                                    {isMedcall2Page ? (
                                      <MedcallAdministrationNotesEditor
                                        canEdit={isMedcall2Page ? Boolean(bootstrap.hasMedcallPowerAccess) : hasFullAccess}
                                        disabled={!isMedcall2Page && pendingAction !== null}
                                        initialNotes={injectionInfo?.administration_notes}
                                        onSave={(administrationNotes) => handleSaveMedicationAdministrationNotes(
                                          timeline,
                                          administrationNotes
                                        )}
                                        preserveLegacySemantics={isMedcall2Page}
                                        timelineId={timeline.id}
                                      />
                                    ) : normalizeValue(injectionInfo?.administration_notes) || '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      ) : (
                        <table className="w-full min-w-[760px] text-sm">
                          <thead>
                            <tr className="border-b border-[var(--neutral-gray-30)] text-left text-xs uppercase tracking-wide text-[var(--neutral-gray-90)]">
                              <th className="py-2 pr-3 font-semibold">Date</th>
                              {!isMedcall2Page ? <th className="py-2 pr-3 font-semibold">Time</th> : null}
                              <th className="py-2 pr-3 font-semibold">{isMedcall2Page ? 'Frequency' : 'Dose'}</th>
                              <th className="py-2 pr-3 font-semibold">Status</th>
                              <th className="py-2 pr-0 font-semibold">{isImOtherMedication ? 'Site' : 'Witnessed By Other'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {timelinePage.rows.map((timeline) => (
                              <tr className="border-b border-[var(--neutral-gray-20)] align-top last:border-b-0" key={timeline.id}>
                                <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">{isMedcall2Page ? formatDate(timeline.date).replace(/\/(\d{2})(\d{2})$/, '/$2') : formatDate(timeline.date)}</td>
                                {!isMedcall2Page ? (
                                  <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">{formatTimeLabel(timeline.time)}</td>
                                ) : null}
                                <td className="py-2 pr-3 text-[var(--neutral-gray-120)]">
                                  {isMedcall2Page
                                    ? getLegacyMedcallTimelineFrequencyLabel(medication)
                                    : formatMedicationTimelineDoseLabel(medication, timeline)}
                                </td>
                                <td className="py-2 pr-3">
                                  {canEditMedicationTimelineStatus ? (
                                    <Select
                                      disabled={pendingAction !== null || (isMedcall2Page && (
                                        isMedicationRunOut || normalizeValue(timeline.status) === 'future'
                                      ))}
                                      onValueChange={(value) => handleSaveMedicationTimelineStatus(
                                        timeline,
                                        value as 'notTaken' | 'taken' | 'unset'
                                      )}
                                      value={normalizeValue(timeline.status) || 'unset'}
                                    >
                                      <SelectTrigger className="h-8 w-[140px]">
                                        <SelectValue placeholder="Status" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="unset">Unset</SelectItem>
                                        <SelectItem value="taken">Taken</SelectItem>
                                        <SelectItem value="notTaken">Not Taken</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <div className="text-[var(--neutral-gray-120)]">
                                      {isMedcall2Page
                                        ? timeline.status == null ? 'unset' : `${timeline.status}`
                                        : formatTimelineStatusLabel(timeline.status)}
                                    </div>
                                  )}
                                </td>
                                <td className="py-2 pr-0">
                                  {isImOtherMedication ? (
                                    canEditMedicationTimelineSite ? (
                                      <Select
                                        disabled={pendingAction !== null}
                                        onValueChange={(value) => handleSaveMedicationTimelineSite(
                                          timeline,
                                          value === SELECT_EMPTY_VALUE ? '' : value
                                        )}
                                      value={normalizeValue(timeline.site) || SELECT_EMPTY_VALUE}
                                    >
                                      <SelectTrigger className="h-8 w-[170px]">
                                        <SelectValue placeholder="Site" />
                                      </SelectTrigger>
                                      <SelectContent>
                                          {MEDICATION_TIMELINE_SITE_OPTIONS.map((option) => (
                                            <SelectItem
                                              key={option.value || SELECT_EMPTY_VALUE}
                                              value={option.value || SELECT_EMPTY_VALUE}
                                            >
                                              {option.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <div className="text-[var(--neutral-gray-120)]">{normalizeValue(timeline.site) || 'Not Set'}</div>
                                    )
                                  ) : canEditMedicationTimelineWitness ? (
                                    <Select
                                      disabled={
                                        normalizeValue(timeline.status) === 'notTaken'
                                        || normalizeValue(timeline.status) === 'unset'
                                        || pendingAction !== null
                                        || (isMedcall2Page && isMedicationRunOut)
                                      }
                                      onValueChange={(value) => handleSaveMedicationTimelineWitness(timeline, value === 'true')}
                                      value={timeline.witnessed_by_other ? 'true' : 'false'}
                                    >
                                      <SelectTrigger className="h-8 w-[150px]">
                                        <SelectValue placeholder="Witnessed" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="false">No</SelectItem>
                                        <SelectItem value="true">Yes</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <div className="text-[var(--neutral-gray-120)]">
                                      {timeline.witnessed_by_other ? 'Yes' : 'No'}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {isMedcall2Page && timelinePage.pageCount > 1 ? (
                        <nav
                          aria-label="Medication timeline pages"
                          className="mt-3 flex items-center justify-end gap-2"
                        >
                          <Button
                            aria-label="Previous medication timeline page"
                            disabled={timelinePage.page <= 1}
                            id={`medcall-medication-${medication.id}-timeline-previous`}
                            onClick={() => setMedicationTimelinePages((current) => (
                              new Map(current).set(medication.id, timelinePage.page - 1)
                            ))}
                            size="sm"
                            type="button"
                            variant="outline"
                          >
                            <ChevronLeft aria-hidden="true" className="h-3.5 w-3.5" />
                          </Button>
                          <span className="text-sm text-[var(--neutral-gray-100)]">
                            {timelinePage.page} / {timelinePage.pageCount}
                          </span>
                          <Button
                            aria-label="Next medication timeline page"
                            disabled={timelinePage.page >= timelinePage.pageCount}
                            id={`medcall-medication-${medication.id}-timeline-next`}
                            onClick={() => setMedicationTimelinePages((current) => (
                              new Map(current).set(medication.id, timelinePage.page + 1)
                            ))}
                            size="sm"
                            type="button"
                            variant="outline"
                          >
                            <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
                          </Button>
                        </nav>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
            </MedicationColumn>
            ))}
          </CardContent>
        </Card>

        {!isMedcall2Page ? (
          <Card>
            <CardHeader>
              <CardTitle>Activation History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              {activationHistory.length < 1 ? (
                <div className="text-sm text-[var(--neutral-gray-100)]">No status history recorded.</div>
              ) : null}

              {activationHistory.map((item, index) => (
                <div
                  key={`${item.date || 'history'}-${index}`}
                  className="border-l-2 border-[var(--neutral-gray-30)] pl-3"
                >
                  <div className="text-sm font-medium capitalize text-[var(--neutral-gray-120)]">
                    {normalizeValue(item.status) || 'Status update'}
                  </div>
                  <div className="text-sm text-[var(--neutral-gray-100)]">
                    {normalizeValue(item.fullname) || 'Unknown user'}
                    {normalizeValue(item.username) ? ` (${normalizeValue(item.username)})` : ''}
                  </div>
                  <div className="text-sm text-[var(--neutral-gray-100)]">{formatDateTime(item.date)}</div>
                  {normalizeValue(item.deactivation_reason) ? (
                    <div className="mt-1 text-sm text-[var(--neutral-gray-120)]">
                      Reason: {normalizeValue(item.deactivation_reason)}
                    </div>
                  ) : null}
                </div>
              ))}
            </CardContent>
          </Card>
        ) : null}
      </div>

      {!isMedcall2Page ? (
        <Card>
          <CardHeader>
            <CardTitle>Client Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            {notes.length < 1 ? (
              <div className="text-sm text-[var(--neutral-gray-100)]">No notes recorded.</div>
            ) : null}

            {notes.map((note) => (
              <div key={note.id} className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] p-3">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium text-[var(--neutral-gray-120)]">
                    {normalizeValue(note.entryby) || 'MedCall'}
                  </div>
                  <div className="text-sm text-[var(--neutral-gray-100)]">{formatDateTime(note.entrydate)}</div>
                </div>
                <div className="whitespace-pre-wrap text-sm text-[var(--neutral-gray-120)]">
                  {normalizeValue(note.notes) || '—'}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={activeDialog === 'edit'} onOpenChange={(open: boolean) => {
        if (!open && pendingAction !== null) return;
        if (!open) {
          closeDialog();
        }
      }}>
        <DialogContent
          className={isMedcall2Page ? 'medcall2-legacy-basic-dialog medcall2-identity-dialog max-w-2xl p-0' : 'max-w-2xl p-0'}
          contentHeight="auto"
          contentMaxHeight="90vh"
          contentMaxWidth={isMedcall2Page ? 'calc(100vw - 16px)' : '900px'}
          contentTop={isMedcall2Page ? '100px' : undefined}
          contentWidth={isMedcall2Page ? '520px' : '760px'}
          hideCloseButton={isMedcall2Page}
          onEscapeKeyDown={isMedcall2Page ? (event) => event.preventDefault() : undefined}
          onOpenAutoFocus={isMedcall2Page ? (event) => { event.preventDefault(); (event.target as HTMLElement).focus(); } : undefined}
          overlayClassName={isMedcall2Page ? 'medcall2-legacy-assignment-dialog__overlay' : undefined}
        >
          <form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleSaveProfile}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Edit Client</DialogTitle>
              {identityDialogCopy.profileDescription ? (
                <DialogDescription>{identityDialogCopy.profileDescription}</DialogDescription>
              ) : null}
            </DialogHeader>
            <div className={`grid gap-4 overflow-y-auto px-5 py-4 ${isMedcall2Page ? '' : 'md:grid-cols-2'}`}>
              {hasFullAccess ? (
                <DialogField label="Program" required>
                  <Select
                    disabled={pendingAction !== null}
                    onValueChange={(value) => {
                      setProfileDraft((current) => {
                        const nextProgram = value === SELECT_EMPTY_VALUE ? '' : value;
                        if (!participantProgramUsesFacility(nextProgram)) {
                          return {
                            ...current,
                            optionFacilityId: '',
                            program: nextProgram,
                          };
                        }

                        return {
                          ...current,
                          program: nextProgram,
                        };
                      });
                    }}
                    value={profileDraft.program || undefined}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select program" />
                    </SelectTrigger>
                    <SelectContent>
                      {programOptions.map((option) => (
                        <SelectItem
                          key={`program-${normalizeValue(option.value)}`}
                          value={normalizeValue(option.value)}
                        >
                          {normalizeValue(option.label) || normalizeValue(option.value)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </DialogField>
              ) : null}

              {profileProgramUsesFacility ? (
                <DialogField label="Facility" required>
                  <Select
                    disabled={pendingAction !== null}
                    onValueChange={(value) => {
                      setProfileDraft((current) => ({
                        ...current,
                        optionFacilityId: value === SELECT_EMPTY_VALUE ? '' : value,
                      }));
                    }}
                    value={profileDraft.optionFacilityId || undefined}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select facility" />
                    </SelectTrigger>
                    <SelectContent>
                      {facilityOptions.map((option) => (
                        <SelectItem
                          key={`facility-${normalizeValue(option.value)}`}
                          value={normalizeValue(option.value)}
                        >
                          {normalizeValue(option.label) || normalizeValue(option.value)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </DialogField>
              ) : null}

              <DialogField label="Provider">
                <Select
                  disabled={pendingAction !== null}
                  onValueChange={(value) => {
                    setProfileDraft((current) => ({
                      ...current,
                      provider: value === SELECT_EMPTY_VALUE ? '' : value,
                    }));
                  }}
                  value={isMedcall2Page
                    ? profileDraft.provider || undefined
                    : profileDraft.provider || SELECT_EMPTY_VALUE}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {!isMedcall2Page ? (
                      <SelectItem value={SELECT_EMPTY_VALUE}>Unassigned</SelectItem>
                    ) : null}
                    {providerOptions.map((option) => (
                      <SelectItem key={`provider-${option.value}`} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </DialogField>

              {hasFullAccess ? (
                <DialogField label="CID" required>
                  <Input
                    disabled={pendingAction !== null}
                    inputMode="numeric"
                    onChange={(event) => {
                      setProfileDraft((current) => ({
                        ...current,
                        cid: normalizeMedcallProfileCidDraft(routeBase, event.target.value),
                      }));
                    }}
                    value={profileDraft.cid}
                  />
                </DialogField>
              ) : null}

              {hasFullAccess ? (
                <DialogField label={identityDialogCopy.dobLabel} required>
                  <Input
                    disabled={pendingAction !== null}
                    onChange={(event) => {
                      setProfileDraft((current) => ({
                        ...current,
                        dob: event.target.value,
                      }));
                    }}
                    type="date"
                    value={profileDraft.dob}
                  />
                </DialogField>
              ) : null}
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {identityDialogCopy.profileSubmitLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'staff'} onOpenChange={(open: boolean) => {
        if (!open && pendingAction !== null) return;
        if (!open) {
          closeDialog();
        }
      }}>
        <DialogContent
          className={isMedcall2Page ? 'medcall2-legacy-assignment-dialog p-0' : 'max-w-2xl p-0'}
          contentHeight="auto"
          contentMaxHeight="90vh"
          contentMaxWidth={isMedcall2Page ? '520px' : '900px'}
          contentTop={isMedcall2Page ? '100px' : undefined}
          contentWidth={isMedcall2Page ? '520px' : '720px'}
          hideCloseButton={isMedcall2Page}
          overlayClassName={isMedcall2Page ? 'medcall2-legacy-assignment-dialog__overlay' : undefined}
        >
          {isMedcall2Page ? (
            <>
              <DialogHeader className="medcall2-legacy-assignment-dialog__header">
                <DialogTitle>Staff Assignment</DialogTitle>
              </DialogHeader>
              <Medcall2StaffAssignmentForm
                currentUserId={bootstrap.medcallUserId}
                history={staffHistory}
                onCancel={closeDialog}
                onChange={setStaffDraft}
                onSubmit={handleSaveStaffAssignment}
                options={availableStaffOptions}
                pending={pendingAction !== null}
                value={staffDraft}
              />
            </>
          ) : (
          <div className="flex max-h-[90vh] flex-col overflow-hidden">
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Staff Assignment</DialogTitle>
              <DialogDescription>Update the assigned staff member for this client.</DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto px-5 py-4">
              {hasFullAccess && staffHistory.length > 0 ? (
                <Tabs onValueChange={setStaffDialogTab} value={staffDialogTab}>
                  <TabsList className="mb-4 inline-flex h-auto rounded-[var(--border-radius-medium)] bg-[var(--surface-muted)] p-1">
                    <TabsTrigger className="rounded-[var(--border-radius-small)] px-3 py-1.5" value="form">
                      Form
                    </TabsTrigger>
                    <TabsTrigger className="rounded-[var(--border-radius-small)] px-3 py-1.5" value="history">
                      History
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent className="mt-0" value="form">
                    <form className="space-y-4" onSubmit={handleSaveStaffAssignment}>
                      <DialogField label="Staff" required>
                        <Select
                          disabled={pendingAction !== null} onValueChange={setStaffDraft} value={staffDraft || undefined}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select staff" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableStaffOptions.map((option) => (
                              <SelectItem key={`staff-${option.id}`} value={`${option.id}`}>
                                {getMedcallStaffOptionLabel(option)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </DialogField>
                      <DialogFooter className="px-0 pt-2">
                        <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                          Cancel
                        </Button>
                        <Button disabled={pendingAction !== null} type="submit">
                          {pendingAction === 'staff' ? 'Saving...' : 'Save Assignment'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </TabsContent>
                  <TabsContent className="mt-0 space-y-3" value="history">
                    {staffHistory.map((item, index) => (
                      <div
                        key={`${item.date || 'staff-history'}-${index}`}
                        className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] px-3 py-2"
                      >
                        <div className="text-sm font-medium text-[var(--neutral-gray-120)]">
                          {normalizeValue(item.fullname) || 'Unknown user'}
                          {normalizeValue(item.username) ? ` (${normalizeValue(item.username)})` : ''}
                        </div>
                        <div className="text-sm text-[var(--neutral-gray-100)]">{formatDateTime(item.date)}</div>
                      </div>
                    ))}
                  </TabsContent>
                </Tabs>
              ) : (
                <form className="space-y-4" onSubmit={handleSaveStaffAssignment}>
                  <DialogField label="Staff" required>
                    <Select
                      disabled={pendingAction !== null} onValueChange={setStaffDraft} value={staffDraft || undefined}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select staff" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableStaffOptions.map((option) => (
                          <SelectItem key={`staff-${option.id}`} value={`${option.id}`}>
                            {getMedcallStaffOptionLabel(option)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </DialogField>
                  <DialogFooter className="px-0 pt-2">
                    <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                      Cancel
                    </Button>
                    <Button disabled={pendingAction !== null} type="submit">
                      {pendingAction === 'staff' ? 'Saving...' : 'Save Assignment'}
                    </Button>
                  </DialogFooter>
                </form>
              )}
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'nurse'} onOpenChange={(open: boolean) => {
        if (!open && (pendingAction !== null || personAssignmentSubmitting)) return;
        if (!open) {
          closeDialog();
        }
      }}>
        <DialogContent
          className={isMedcall2Page ? 'medcall2-legacy-assignment-dialog p-0' : 'max-w-2xl p-0'}
          contentHeight="auto"
          contentMaxHeight="90vh"
          contentMaxWidth={isMedcall2Page ? '520px' : '900px'}
          contentTop={isMedcall2Page ? '100px' : undefined}
          contentWidth={isMedcall2Page ? '520px' : '720px'}
          hideCloseButton={isMedcall2Page}
          overlayClassName={isMedcall2Page ? 'medcall2-legacy-assignment-dialog__overlay' : undefined}
        >
          {isMedcall2Page ? (
            <>
              <DialogHeader className="medcall2-legacy-assignment-dialog__header"><DialogTitle>Nurse Assignment</DialogTitle></DialogHeader>
              <Medcall2PersonAssignmentForm
                disabled={pendingAction !== null}
                draft={nurseDraft}
                filterDomains={filterDomains}
                kind="nurse"
                onSubmittingChange={setPersonAssignmentSubmitting}
                onCancel={closeDialog}
                onDraftChange={setNurseDraft}
                onValidSubmit={(values) => savePersonAssignment('nurse', values)}
              />
            </>
          ) : (
          <form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleSaveNurse}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Nurse Assignment</DialogTitle>
              <DialogDescription>Update the nurse contact for this client.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 overflow-y-auto px-5 py-4 md:grid-cols-2">
              <DialogField label="First Name" required>
                <Input
                  disabled={pendingAction !== null}
                  onChange={(event) => {
                    setNurseDraft((current) => ({
                      ...current,
                      firstname: event.target.value,
                    }));
                  }}
                  value={nurseDraft.firstname}
                />
              </DialogField>
              <DialogField label="Last Name" required>
                <Input
                  disabled={pendingAction !== null}
                  onChange={(event) => {
                    setNurseDraft((current) => ({
                      ...current,
                      lastname: event.target.value,
                    }));
                  }}
                  value={nurseDraft.lastname}
                />
              </DialogField>
              <DialogField label="Title" required>
                <Select
                  disabled={pendingAction !== null}
                  onValueChange={(value) => {
                    setNurseDraft((current) => ({
                      ...current,
                      title: value,
                    }));
                  }}
                  value={nurseDraft.title || undefined}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select title" />
                  </SelectTrigger>
                  <SelectContent>
                    {NURSE_TITLES.map((title) => (
                      <SelectItem key={`nurse-title-${title}`} value={title}>
                        {title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </DialogField>
              <DialogField label="Email" required>
                <Input
                  disabled={pendingAction !== null}
                  onChange={(event) => {
                    setNurseDraft((current) => ({
                      ...current,
                      email: event.target.value,
                    }));
                  }}
                  type="email"
                  value={nurseDraft.email}
                />
              </DialogField>
              <DialogField label="Retype Email" required>
                <Input
                  disabled={pendingAction !== null}
                  onChange={(event) => {
                    setNurseDraft((current) => ({
                      ...current,
                      confirmEmail: event.target.value,
                    }));
                  }}
                  type="email"
                  value={nurseDraft.confirmEmail}
                />
              </DialogField>
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {pendingAction === 'nurse' ? 'Saving...' : 'Save Nurse'}
              </Button>
            </DialogFooter>
          </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'psychiatrist'} onOpenChange={(open: boolean) => {
        if (!open && (pendingAction !== null || personAssignmentSubmitting)) return;
        if (!open) {
          closeDialog();
        }
      }}>
        <DialogContent
          className={isMedcall2Page ? 'medcall2-legacy-assignment-dialog p-0' : 'max-w-2xl p-0'}
          contentHeight="auto"
          contentMaxHeight="90vh"
          contentMaxWidth={isMedcall2Page ? '520px' : '900px'}
          contentTop={isMedcall2Page ? '100px' : undefined}
          contentWidth={isMedcall2Page ? '520px' : '720px'}
          hideCloseButton={isMedcall2Page}
          overlayClassName={isMedcall2Page ? 'medcall2-legacy-assignment-dialog__overlay' : undefined}
        >
          {isMedcall2Page ? (
            <>
              <DialogHeader className="medcall2-legacy-assignment-dialog__header"><DialogTitle>Psychiatrist Assignment</DialogTitle></DialogHeader>
              <Medcall2PersonAssignmentForm
                disabled={pendingAction !== null}
                draft={psychiatristDraft}
                filterDomains={filterDomains}
                kind="psychiatrist"
                onSubmittingChange={setPersonAssignmentSubmitting}
                onCancel={closeDialog}
                onDraftChange={setPsychiatristDraft}
                onValidSubmit={(values) => savePersonAssignment('psychiatrist', values)}
              />
            </>
          ) : (
          <form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleSavePsychiatrist}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Psychiatrist Assignment</DialogTitle>
              <DialogDescription>Update the psychiatrist contact for this client.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 overflow-y-auto px-5 py-4 md:grid-cols-2">
              <DialogField label="First Name" required>
                <Input
                  disabled={pendingAction !== null}
                  onChange={(event) => {
                    setPsychiatristDraft((current) => ({
                      ...current,
                      firstname: event.target.value,
                    }));
                  }}
                  value={psychiatristDraft.firstname}
                />
              </DialogField>
              <DialogField label="Last Name" required>
                <Input
                  disabled={pendingAction !== null}
                  onChange={(event) => {
                    setPsychiatristDraft((current) => ({
                      ...current,
                      lastname: event.target.value,
                    }));
                  }}
                  value={psychiatristDraft.lastname}
                />
              </DialogField>
              <DialogField label="Title" required>
                <Select
                  disabled={pendingAction !== null}
                  onValueChange={(value) => {
                    setPsychiatristDraft((current) => ({
                      ...current,
                      title: value,
                    }));
                  }}
                  value={psychiatristDraft.title || undefined}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select title" />
                  </SelectTrigger>
                  <SelectContent>
                    {PSYCHIATRIST_TITLES.map((title) => (
                      <SelectItem key={`psychiatrist-title-${title}`} value={title}>
                        {title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </DialogField>
              <DialogField label="Email">
                <Input
                  disabled={pendingAction !== null}
                  onChange={(event) => {
                    setPsychiatristDraft((current) => ({
                      ...current,
                      email: event.target.value,
                    }));
                  }}
                  type="email"
                  value={psychiatristDraft.email}
                />
              </DialogField>
              <DialogField label="Retype Email">
                <Input
                  disabled={pendingAction !== null}
                  onChange={(event) => {
                    setPsychiatristDraft((current) => ({
                      ...current,
                      confirmEmail: event.target.value,
                    }));
                  }}
                  type="email"
                  value={psychiatristDraft.confirmEmail}
                />
              </DialogField>
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {pendingAction === 'psychiatrist' ? 'Saving...' : 'Save Psychiatrist'}
              </Button>
            </DialogFooter>
          </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={activeDialog === 'aliases'} onOpenChange={(open: boolean) => {
        if (!open && pendingAction !== null) return;
        if (!open) {
          closeDialog();
        }
      }}>
        <DialogContent
          aria-describedby={undefined}
          className={isMedcall2Page ? 'medcall2-legacy-basic-dialog medcall2-identity-dialog medcall2-alias-dialog max-w-2xl p-0' : 'max-w-2xl p-0'}
          contentHeight="auto"
          contentMaxHeight="90vh"
          contentMaxWidth={isMedcall2Page ? 'calc(100vw - 16px)' : '760px'}
          contentTop={isMedcall2Page ? '100px' : undefined}
          contentWidth={isMedcall2Page ? '520px' : '620px'}
          hideCloseButton={isMedcall2Page}
          onEscapeKeyDown={isMedcall2Page ? (event) => event.preventDefault() : undefined}
          onOpenAutoFocus={isMedcall2Page ? (event) => { event.preventDefault(); (event.target as HTMLElement).focus(); } : undefined}
          overlayClassName={isMedcall2Page ? 'medcall2-legacy-assignment-dialog__overlay' : undefined}
        >
          <form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleAddAlias}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Client Aliases</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 overflow-y-auto px-5 py-4">
              {isMedcall2Page ? (
                <ol className="medcall2-alias-timeline">
                  <li className="medcall2-alias-timeline__heading"><strong>Aliases</strong></li>
                  {participant.aliases && participant.aliases.length > 0
                    ? participant.aliases.map((alias, index) => (
                      <li key={`participant-alias-${index + 1}`}>{alias.firstname} {alias.lastname}</li>
                    ))
                    : <li className="medcall2-alias-timeline__empty">None</li>}
                </ol>
              ) : (
              <div className="space-y-2">
                <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">Aliases</div>
                {participant.aliases && participant.aliases.length > 0 ? (
                  <ol className="space-y-2 border-l border-[var(--neutral-gray-40)] pl-5">
                    {participant.aliases.map((alias, index) => (
                      <li className="relative text-sm text-[var(--neutral-gray-120)]" key={`participant-alias-${index + 1}`}>
                        <span className="absolute -left-[24px] top-1.5 h-2 w-2 rounded-full bg-[var(--neutral-gray-60)]" />
                        {[alias.firstname, alias.lastname].filter(Boolean).join(' ').trim()}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div className="text-sm text-[var(--neutral-gray-100)]">None</div>
                )}
              </div>
              )}
              <div className="text-sm font-semibold text-[var(--neutral-gray-120)]">Add an alias:</div>
              {showAliasEntryFields ? (
                <div className={`grid gap-4 ${isMedcall2Page ? '' : 'md:grid-cols-2'}`}>
                  <DialogField label="Firstname" required>
                    <Input
                      disabled={pendingAction !== null}
                      onChange={(event) => setAliasDraft((current) => ({ ...current, firstname: event.target.value }))}
                      value={aliasDraft.firstname}
                    />
                  </DialogField>
                  <DialogField label="Lastname" required>
                    <Input
                      disabled={pendingAction !== null}
                      onChange={(event) => setAliasDraft((current) => ({ ...current, lastname: event.target.value }))}
                      value={aliasDraft.lastname}
                    />
                  </DialogField>
                </div>
              ) : null}
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {pendingAction === 'alias' && !isMedcall2Page ? 'Saving...' : 'Submit'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {isMedcall2Page ? (
        <Medcall2ClientNotesDialog
          noteDraft={noteDraft}
          notes={notes}
          onAddNote={handleAddNote}
          onClose={closeDialog}
          onDeleteNote={handleDeleteNote}
          onNoteDraftChange={(event) => setNoteDraft(event.target.value)}
          open={activeDialog === 'notes'}
          pending={pendingAction !== null}
        />
      ) : null}

      <Dialog open={!isMedcall2Page && activeDialog === 'notes'} onOpenChange={(open: boolean) => {
        if (!open && pendingAction !== null) return;
        if (!open) {
          closeDialog();
        }
      }}>
        <DialogContent
          className={isMedcall2Page ? 'medcall2-legacy-basic-dialog max-w-4xl p-0' : 'max-w-4xl p-0'}
          contentHeight="auto"
          contentMaxHeight="90vh"
          contentMaxWidth="960px"
          contentWidth="820px"
        >
          <div className="flex max-h-[90vh] flex-col overflow-hidden">
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Client Notes</DialogTitle>
              <DialogDescription>Review the note history and add a new note.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto px-5 py-4">
              <form className="space-y-3" onSubmit={handleAddNote}>
                <DialogField label="New Note">
                  <Textarea
                    disabled={pendingAction !== null}
                    onChange={(event) => setNoteDraft(event.target.value)}
                    placeholder="Enter a note"
                    rows={3}
                    value={noteDraft}
                  />
                </DialogField>
                <div className="flex justify-end">
                  <Button disabled={pendingAction !== null || !normalizeValue(noteDraft)} type="submit">
                    {pendingAction === 'note' ? 'Saving...' : 'Add Note'}
                  </Button>
                </div>
              </form>
              <div className="space-y-3">
                {notes.length < 1 ? (
                  <div className="text-sm text-[var(--neutral-gray-100)]">No notes recorded for this client.</div>
                ) : null}
                {notes.map((note) => (
                  <div
                    key={`dialog-note-${note.id}`}
                    className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] px-3 py-3"
                  >
                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm font-medium text-[var(--neutral-gray-120)]">
                        {normalizeValue(note.entryby) || 'MedCall'}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-[var(--neutral-gray-100)]">{formatDateTime(note.entrydate)}</div>
                        <Button
                          aria-label="Delete this note"
                          className="h-7 w-7 p-0 text-[var(--semantic-error)]"
                          disabled={pendingAction !== null}
                          onClick={() => setDeleteNoteTarget(note)}
                          size="sm"
                          type="button"
                          variant="ghost"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="whitespace-pre-wrap text-sm text-[var(--neutral-gray-120)]">
                      {normalizeValue(note.notes) || '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                Close
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <YesNoDialog
        cancelLabel="Cancel"
        confirmClassName="border-[var(--semantic-error)] bg-[var(--semantic-error)] hover:opacity-90"
        confirmDisabled={pendingAction !== null}
        confirmLabel="Delete"
        description="Delete this note?"
        onConfirm={(event?: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          void handleDeleteNote();
        }}
        onOpenChange={(open) => {
          if (open || pendingAction !== null) {
            return;
          }

          setDeleteNoteTarget(null);
        }}
        open={Boolean(deleteNoteTarget)}
        title="Delete this note?"
      />

      <MedcallSendMessageDialog
        onOpenChange={(open) => {
          if (open) {
            setActiveDialog('message');
            return;
          }

          closeDialog();
        }}
        open={activeDialog === 'message'}
        participant={participant}
        participantId={participant.id}
        preserveLegacySemantics={routeBase === '/tools/medcall2'}
        routeBase={embedded ? undefined : routeBase}
      />

      <MedcallAdherenceReportDialog
        legacyPresentation={isMedcall2Page}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
        open={activeDialog === 'report'}
        participant={participant}
        routeBase={embedded ? undefined : routeBase}
      />

      <MedcallMedicationListDialog
        legacyPresentation={isMedcall2Page}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
        open={activeDialog === 'med-list'}
        participant={participant}
      />

      <Dialog open={activeDialog === 'medication'} onOpenChange={(open: boolean) => {
        if (!open && pendingAction !== null) return;
        if (!open) {
          closeDialog();
        }
      }}>
        <DialogContent
          className={isMedcall2Page ? 'medcall2-legacy-medication-dialog p-0' : 'max-w-4xl p-0'}
          contentHeight="auto"
          contentMaxHeight="90vh"
          contentMaxWidth={isMedcall2Page ? '520px' : '980px'}
          contentWidth={isMedcall2Page ? '520px' : '860px'}
          hideCloseButton={isMedcall2Page}
          overlayClassName={isMedcall2Page ? 'medcall2-legacy-medication-dialog__overlay' : undefined}
        >
          {isMedcall2Page && medicationPreviewVariables ? (
            <div
              className="flex max-h-[90vh] flex-col overflow-hidden"
              data-testid="medcall2-medication-confirmation"
            >
              <DialogHeader className={isMedcall2Page ? 'medcall2-legacy-medication-dialog__header' : 'border-b border-[var(--neutral-gray-40)] px-5 py-4'}>
                <DialogTitle>{isMedcall2Page ? 'Confirmation' : editingMedicationId ? 'Edit Medication' : 'Add Medication'}</DialogTitle>
                {!isMedcall2Page ? <DialogDescription>Review the medication details before submitting.</DialogDescription> : null}
              </DialogHeader>
              <dl className="grid overflow-y-auto border-b border-[var(--neutral-gray-40)] px-5 py-4">
                {medicationConfirmationRows.map((row) => (
                  <div
                    className="grid grid-cols-[minmax(150px,0.35fr)_1fr] border border-b-0 border-[var(--neutral-gray-40)] last:border-b"
                    key={row.label}
                  >
                    <dt className="border-r border-[var(--neutral-gray-40)] bg-[var(--surface-muted)] px-3 py-2 font-semibold">
                      {row.label}
                    </dt>
                    <dd className="px-3 py-2">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
                <Button
                  disabled={pendingAction !== null}
                  id="medcall-medication-preview-cancel-button"
                  onClick={() => setMedicationPreviewVariables(null)}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  disabled={pendingAction !== null}
                  id="medcall-medication-preview-submit-button"
                  onClick={() => void handleConfirmMedicationSave()}
                  type="button"
                >
                  {pendingAction === 'medication-create' || pendingAction === 'medication-update'
                    ? 'Saving...'
                    : 'Submit'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form className={isMedcall2Page ? 'medcall2-legacy-medication-dialog__form' : 'flex max-h-[90vh] flex-col overflow-hidden'} onSubmit={handleSaveMedication}>
            <DialogHeader className={isMedcall2Page ? 'medcall2-legacy-medication-dialog__header' : 'border-b border-[var(--neutral-gray-40)] px-5 py-4'}>
              <DialogTitle>{isMedcall2Page ? editingMedicationId ? 'Update Medication' : 'Add Medication' : editingMedicationId ? 'Edit Medication' : 'Add Medication'}</DialogTitle>
              {!isMedcall2Page ? <DialogDescription>Update the medication schedule and related details for this client.</DialogDescription> : null}
            </DialogHeader>
            <div className={isMedcall2Page
              ? `medcall2-legacy-medication-dialog__fields${showLegacyMedicationSearchOnly ? ' is-search-only' : showLegacyMedicationDoseOnly ? ' is-dose-only' : ''}`
              : 'grid gap-4 overflow-y-auto px-5 py-4 md:grid-cols-2'}>
              <DialogField label={showLegacyMedicationSearchOnly ? 'Medicine Search' : isMedcall2Page ? 'Medication' : 'Medication Name'} required={!isMedcall2Page}>
                <div className="relative">
                  <Input
                    autoComplete="off"
                    disabled={medicationFieldsPending || (isMedcall2Page && !showLegacyMedicationSearchOnly)}
                    onChange={(event) => {
                      if (verifyCatalogLookup) latestMedicationStrengthRequest.invalidate();
                      setMedicationDraft((current) => ({
                        ...current,
                        medicationExternalId: '',
                        medicineDetail: '',
                        medicineName: event.target.value,
                        strength: '',
                      }));
                      setMedicationStrengthOptions([]);
                      setUsesCustomMedicationStrength(false);
                    }}
                    placeholder="Please enter 4 or more charachters"
                    role={isMedcall2Page ? 'combobox' : undefined}
                    type={isMedcall2Page ? 'search' : undefined}
                    value={medicationDraft.medicineName}
                  />
                  {medicationSearchLoading ? (
                    <div className="mt-1 text-xs text-[var(--neutral-gray-90)]">Searching medications...</div>
                  ) : null}
                  {medicationSearchResults.length > 0 ? (
                    <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded border border-[var(--neutral-gray-40)] bg-white shadow-lg">
                      {medicationSearchResults.map((item, index) => {
                        const itemName = normalizeValue(item.name || item.displayName);
                        const itemKey = normalizeValue(item.externalId || item.uuid || item.drugbankPcid) || `${itemName}-${index}`;
                        return (
                          <button
                            className="block w-full border-b border-[var(--neutral-gray-20)] px-3 py-2 text-left text-sm text-[var(--neutral-gray-120)] hover:bg-[var(--surface-muted)] last:border-b-0"
                            disabled={medicationFieldsPending}
                            key={itemKey}
                            onClick={() => {
                              void handleMedicationCatalogSelection(item);
                            }}
                            type="button"
                          >
                            <span>{itemName}</span>
                            {item.isCustom ? (
                              <span className="ml-2 text-xs text-[var(--neutral-gray-90)]">Custom</span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </DialogField>

              <DialogField label="Dose" required>
                {((isMedcall2Page || medicationStrengthOptions.length > 0) && !usesCustomMedicationStrength) ? (
                  <Select
                    disabled={medicationFieldsPending}
                    onValueChange={(value) => {
                      if (value === '__custom__') {
                        setUsesCustomMedicationStrength(true);
                        setMedicationDraft((current) => ({ ...current, strength: '' }));
                        return;
                      }

                      setMedicationDraft((current) => ({
                        ...current,
                        strength: resolveMedcallStoredStrength(medicationStrengthOptions, value),
                      }));
                    }}
                    value={resolveMedcallStrengthControlValue(
                      medicationStrengthOptions,
                      medicationDraft.strength
                    ) || undefined}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select dose" />
                    </SelectTrigger>
                    <SelectContent>
                      {medicationDraft.strength && !medicationStrengthOptions.some((option) => (
                        getMedcallStrengthOptionLabel(option) === medicationDraft.strength
                          || getMedcallStrengthOptionValue(option) === medicationDraft.strength
                      )) ? (
                        <SelectItem value={medicationDraft.strength}>{medicationDraft.strength}</SelectItem>
                      ) : null}
                      {medicationStrengthOptions.map((option, index) => {
                        const strengthOptionValue = getMedcallStrengthOptionValue(option);
                        const strengthOptionLabel = getMedcallStrengthOptionLabel(option);
                        return (
                          <SelectItem
                            key={`medication-strength-${strengthOptionValue}-${index}`}
                            value={strengthOptionValue}
                          >
                            {strengthOptionLabel}
                          </SelectItem>
                        );
                      })}
                      <SelectItem value="__custom__">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="space-y-1">
                    <Input
                      disabled={medicationFieldsPending}
                      onChange={(event) => {
                        setMedicationDraft((current) => ({
                          ...current,
                          strength: event.target.value,
                        }));
                      }}
                      placeholder="Enter dose"
                      value={medicationDraft.strength}
                    />
                    {usesCustomMedicationStrength && medicationStrengthOptions.length > 0 ? (
                      <button
                        className="text-xs text-[var(--primary)] underline"
                        disabled={medicationFieldsPending}
                        onClick={() => {
                          setUsesCustomMedicationStrength(false);
                          setMedicationDraft((current) => ({ ...current, strength: '' }));
                        }}
                        type="button"
                      >
                        Choose a listed dose
                      </button>
                    ) : null}
                  </div>
                )}
              </DialogField>

              <DialogField label="Route" required>
                <Select
                  disabled={medicationFieldsPending}
                  onValueChange={handleMedicationRouteChange}
                  value={medicationDraft.route || undefined}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select route" />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDICATION_ROUTE_OPTIONS.map((option) => (
                      <SelectItem key={`med-route-${option}`} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </DialogField>

              {medicationDraft.route !== 'IM-LAI' ? (
                <DialogField label="PRN">
                  <Select
                    disabled={medicationFieldsPending}
                    onValueChange={(value) => {
                      setMedicationDraft((current) => ({
                        ...current,
                        prn: value === 'true',
                      }));
                    }}
                    value={medicationDraft.prn ? 'true' : 'false'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select PRN option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">No</SelectItem>
                      <SelectItem value="true">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </DialogField>
              ) : null}

              {medicationDraft.route !== 'IM-LAI' ? (
                <DialogField label="Importance">
                  <Select
                    disabled={medicationFieldsPending}
                    onValueChange={(value) => {
                      setMedicationDraft((current) => ({
                        ...current,
                        importanceLevel: value,
                      }));
                    }}
                    value={medicationDraft.importanceLevel || '2'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select importance" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Low</SelectItem>
                      <SelectItem value="1">Medium</SelectItem>
                      <SelectItem value="2">High</SelectItem>
                    </SelectContent>
                  </Select>
                </DialogField>
              ) : null}

              <DialogField label="Frequency" required>
                <Select
                  disabled={medicationFieldsPending}
                  onValueChange={handleMedicationFrequencyChange}
                  value={medicationDraft.frequency || undefined}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencyOptions.map((option) => (
                      <SelectItem key={`med-frequency-${option.value}`} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </DialogField>

              <DialogField label="Schedule" required>
                <Select
                  disabled={medicationFieldsPending}
                  onValueChange={(value) => {
                    if (!value && !embedded && (routeBase === '/tools/medcall' || isMedcall2Page)) return;
                    setMedicationDraft((current) => ({
                      ...current,
                      timesPerDay: value,
                    }));
                  }}
                  value={medicationDraft.timesPerDay || undefined}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select schedule" />
                  </SelectTrigger>
                  <SelectContent>
                    {scheduleOptions.map((option) => (
                      <SelectItem key={`med-schedule-${option}`} value={option}>
                        {formatMedicationScheduleLabel(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </DialogField>

              {medicationDraft.frequency === 'custom_days' ? (
                <div className="space-y-2 md:col-span-2">
                  <div className="text-sm font-medium text-[var(--neutral-gray-120)]">Custom Days</div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                    {MEDICATION_DAY_OPTIONS.map((option) => {
                      const checked = medicationDraft.customDays.includes(option.value);
                      return (
                        <label
                          key={`med-day-${option.value}`}
                          className="flex items-center gap-2 rounded-[var(--border-radius-small)] border border-[var(--neutral-gray-30)] px-3 py-2 text-sm text-[var(--neutral-gray-120)]"
                        >
                          <Checkbox
                            checked={checked}
                            disabled={medicationFieldsPending}
                            onCheckedChange={(value) => handleMedicationDayToggle(option.value, value === true)}
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {medicationDraft.route !== 'IM-LAI' ? (
                <DialogField label="Start Date and Time" required>
                  <Input
                    disabled={medicationFieldsPending}
                    onChange={(event) => {
                      setMedicationDraft((current) => ({
                        ...current,
                        startDate: event.target.value,
                      }));
                    }}
                    type="datetime-local"
                    value={medicationDraft.startDate}
                  />
                </DialogField>
              ) : null}

              {medicationDraft.route !== 'IM-LAI' ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-[var(--neutral-gray-120)]">Discontinue Date</div>
                  <div className="flex items-center justify-between rounded-[var(--border-radius-small)] border border-[var(--neutral-gray-30)] px-3 py-2">
                    <span className="text-sm text-[var(--neutral-gray-120)]">Enable discontinue date</span>
                    <Switch
                      checked={medicationDraft.stopDateEnabled}
                      disabled={medicationFieldsPending}
                      onCheckedChange={(checked) => {
                        setMedicationDraft((current) => ({
                          ...current,
                          stopDate: checked ? current.stopDate : '',
                          stopDateEnabled: checked,
                        }));
                      }}
                    />
                  </div>
                </div>
              ) : null}

              {medicationDraft.route !== 'IM-LAI' && medicationDraft.stopDateEnabled ? (
                <DialogField label="Discontinue Date and Time" required>
                  <Input
                    disabled={medicationFieldsPending}
                    onChange={(event) => {
                      setMedicationDraft((current) => ({
                        ...current,
                        stopDate: event.target.value,
                      }));
                    }}
                    type="datetime-local"
                    value={medicationDraft.stopDate}
                  />
                </DialogField>
              ) : null}

              {medicationDraft.route !== 'IM-LAI' && !isMedicationReadOnlyProgram ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-[var(--neutral-gray-120)]">Runs Out Date</div>
                  <div className="flex items-center justify-between rounded-[var(--border-radius-small)] border border-[var(--neutral-gray-30)] px-3 py-2">
                    <span className="text-sm text-[var(--neutral-gray-120)]">Track runs out date</span>
                    <Switch
                      checked={medicationDraft.refillStatus}
                      disabled={medicationFieldsPending}
                      onCheckedChange={(checked) => {
                        setMedicationDraft((current) => ({
                          ...current,
                          refillDate: checked ? current.refillDate : '',
                          refillStatus: checked,
                        }));
                      }}
                    />
                  </div>
                </div>
              ) : null}

              {medicationDraft.route !== 'IM-LAI' && !isMedicationReadOnlyProgram && medicationDraft.refillStatus ? (
                <DialogField label="Runs Out Date" required>
                  <Input
                    disabled={medicationFieldsPending}
                    onChange={(event) => {
                      setMedicationDraft((current) => ({
                        ...current,
                        refillDate: event.target.value,
                      }));
                    }}
                    type="date"
                    value={medicationDraft.refillDate}
                  />
                </DialogField>
              ) : null}

              {medicationDraft.route === 'IM-LAI' ? (
                <DialogField label="Date Due" required>
                  <Input
                    disabled={medicationFieldsPending}
                    onChange={(event) => {
                      setMedicationDraft((current) => ({
                        ...current,
                        dateDue: event.target.value,
                      }));
                    }}
                    type="date"
                    value={medicationDraft.dateDue}
                  />
                </DialogField>
              ) : null}

              {medicationDraft.route === 'IM-LAI' ? (
                <DialogField label="Injection Status">
                  <Select
                    disabled={medicationFieldsPending}
                    onValueChange={(value) => {
                      setMedicationDraft((current) => ({
                        ...current,
                        dateInjectionArrived: value === '2' ? current.dateInjectionArrived : '',
                        dateGiven: value === '2' ? current.dateGiven : '',
                        onSite: value,
                      }));
                    }}
                    value={medicationDraft.onSite || '0'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select injection status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Not Yet On-Site</SelectItem>
                      <SelectItem value="1">N.A.</SelectItem>
                      <SelectItem value="2">On-Site</SelectItem>
                    </SelectContent>
                  </Select>
                </DialogField>
              ) : null}

              {medicationDraft.route === 'IM-LAI' && medicationDraft.onSite === '2' ? (
                <DialogField label="Date Injection Arrived On-Site" required>
                  <Input
                    disabled={medicationFieldsPending}
                    onChange={(event) => {
                      setMedicationDraft((current) => ({
                        ...current,
                        dateInjectionArrived: event.target.value,
                      }));
                    }}
                    type="date"
                    value={medicationDraft.dateInjectionArrived}
                  />
                </DialogField>
              ) : null}

              {medicationDraft.route === 'IM-LAI' ? (
                <DialogField label="Date Given">
                  <Input
                    disabled={medicationFieldsPending || medicationDraft.onSite !== '2'}
                    onChange={(event) => {
                      setMedicationDraft((current) => ({
                        ...current,
                        administrationNotes: event.target.value ? current.administrationNotes : '',
                        dateGiven: event.target.value,
                      }));
                    }}
                    type="date"
                    value={medicationDraft.dateGiven}
                  />
                </DialogField>
              ) : null}

              {medicationDraft.route === 'IM-LAI' ? (
                <div className="md:col-span-2">
                  <DialogField label="Injection Site Location">
                    <Input
                      disabled={medicationFieldsPending}
                      onChange={(event) => {
                        setMedicationDraft((current) => ({
                          ...current,
                          administrationNotes: event.target.value,
                        }));
                      }}
                      value={medicationDraft.administrationNotes}
                    />
                  </DialogField>
                </div>
              ) : null}
            </div>
            <DialogFooter className={isMedcall2Page ? 'medcall2-legacy-medication-dialog__footer' : 'border-t border-[var(--neutral-gray-40)] px-5 py-4'}>
              {isMedcall2Page ? (
                <Button
                  disabled={pendingAction !== null}
                  id="medcall-medication-cancel-button"
                  onClick={closeDialog}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
              ) : null}
              <Button disabled={pendingAction !== null} id="medcall-medication-save-button" type="submit">
                {!isMedcall2Page && editingMedicationId ? <Save className="h-4 w-4" /> : null}
                {!isMedcall2Page && !editingMedicationId ? <Plus className="h-4 w-4" /> : null}
                {isMedcall2Page
                  ? 'Submit'
                  : pendingAction === 'medication-create' || pendingAction === 'medication-update'
                    ? 'Saving...'
                    : editingMedicationId
                      ? 'Save Medication'
                      : 'Add Medication'}
              </Button>
              {!isMedcall2Page ? <Button
                disabled={pendingAction !== null}
                id="medcall-medication-cancel-button"
                onClick={closeDialog}
                type="button"
                variant="outline"
              >
                Cancel
              </Button> : null}
            </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={projectSitesTrackerSelection !== null} onOpenChange={(open: boolean) => {
        if (!open && pendingAction !== null) return;
        if (!open) {
          setProjectSitesTrackerSelection(null);
        }
      }}>
        <DialogContent
          className={isMedcall2Page ? 'medcall2-legacy-basic-dialog max-w-xl p-0' : 'max-w-xl p-0'}
          contentHeight="auto"
          contentMaxHeight="90vh"
          contentMaxWidth="680px"
          contentWidth="560px"
        >
          <form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleSaveProjectSitesTrackerSelection}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>Update Tracker</DialogTitle>
              <DialogDescription>
                {projectSitesTrackerSelection?.label || 'Select the tracker status for this date range.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto px-5 py-4">
              <DialogField label="Status" required>
                <Select
                  disabled={pendingAction !== null}
                  onValueChange={(value) => setProjectSitesTrackerStatus(value as 'notTaken' | 'taken')}
                  value={projectSitesTrackerStatus}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_SITES_TRACKER_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </DialogField>
              <DialogField label="Notes">
                <Textarea
                  disabled={pendingAction !== null}
                  onChange={(event) => setProjectSitesTrackerNotes(event.target.value)}
                  rows={4}
                  value={projectSitesTrackerNotes}
                />
              </DialogField>
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button
                disabled={pendingAction !== null}
                onClick={() => setProjectSitesTrackerSelection(null)}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {pendingAction === 'project-sites-tracker' ? 'Saving...' : 'Save Tracker'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {isMedcall2Page ? (
        <MedcallImlaiDueDateWarning
          countyHolidays={countyHolidays}
          medications={medications}
          onCancel={() => setDismissedImlaiWarningParticipantId(activeParticipantId)}
          onUpdateAll={handleUpdateAllImlaiDueDates}
          onUpdateDueDate={handleUpdateImlaiDueDate}
          open={activeParticipantId > 0 && dismissedImlaiWarningParticipantId !== activeParticipantId}
          pending={Boolean(pendingAction?.startsWith('imlai-due-date'))}
        />
      ) : null}

      <Dialog open={!isMedcall2Page && activeDialog === 'status'} onOpenChange={(open: boolean) => {
        if (!open && pendingAction !== null) return;
        if (!open) {
          closeDialog();
        }
      }}>
        <DialogContent
          className={isMedcall2Page ? 'medcall2-legacy-basic-dialog max-w-xl p-0' : 'max-w-xl p-0'}
          contentHeight="auto"
          contentMaxHeight="90vh"
          contentMaxWidth="680px"
          contentWidth="560px"
        >
          <form className="flex max-h-[90vh] flex-col overflow-hidden" onSubmit={handleSaveStatus}>
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle>{participant.active ? 'Deactivate Client' : 'Activate Client'}</DialogTitle>
              <DialogDescription>
                {participant.active
                  ? 'Record the reason and date for deactivation.'
                  : 'Confirm that this client should be marked active.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto px-5 py-4">
              {participant.active ? (
                <>
                  <DialogField label="Reason" required>
                    <Select
                      disabled={pendingAction !== null}
                      onValueChange={(value) => {
                        setStatusDraft((current) => ({
                          ...current,
                          reason: value,
                        }));
                      }}
                      value={statusDraft.reason}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEACTIVATION_OPTIONS.map((reason) => (
                          <SelectItem key={`deactivation-${reason}`} value={reason}>
                            {reason}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </DialogField>
                  {statusDraft.reason === 'Other' ? (
                    <DialogField label="Other Reason" required>
                      <Input
                        disabled={pendingAction !== null}
                        onChange={(event) => {
                          setStatusDraft((current) => ({
                            ...current,
                            otherReason: event.target.value,
                          }));
                        }}
                        value={statusDraft.otherReason}
                      />
                    </DialogField>
                  ) : null}
                  <DialogField label="Deactivation Date" required>
                    <Input
                      disabled={pendingAction !== null}
                      max={maxDeactivationDate}
                      min={minDeactivationDate}
                      onChange={(event) => {
                        setStatusDraft((current) => ({
                          ...current,
                          activationBackDate: event.target.value,
                        }));
                      }}
                      type="date"
                      value={statusDraft.activationBackDate}
                    />
                  </DialogField>
                </>
              ) : (
                <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)] bg-[var(--surface-muted)] px-4 py-3 text-sm text-[var(--neutral-gray-120)]">
                  This will mark the client active again.
                </div>
              )}
            </div>
            <DialogFooter className="border-t border-[var(--neutral-gray-40)] px-5 py-4">
              <Button disabled={pendingAction !== null} onClick={closeDialog} type="button" variant="outline">
                Cancel
              </Button>
              <Button disabled={pendingAction !== null} type="submit">
                {pendingAction === 'status'
                  ? 'Saving...'
                  : participant.active
                    ? 'Deactivate Client'
                    : 'Activate Client'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
